let studyTradesFormSecurityConfig;
let studyTradesTurnstileScript;

async function studyTradesLeadRequest(url, options) {
  const destination = new URL(url, window.location.origin);
  if (!["app.studytrades.com", "admin.studytrades.com"].includes(destination.hostname) ||
      !["/api/license-interest", "/api/marketing/lead"].includes(destination.pathname)) return fetch(url, options);
  studyTradesFormSecurityConfig ||= fetch(`${destination.origin}/api/public/form-security`)
    .then((response) => { if (!response.ok) throw new Error("Security verification is unavailable. Please try again."); return response.json(); })
    .catch((error) => { studyTradesFormSecurityConfig = null; throw error; });
  const config = await studyTradesFormSecurityConfig;
  const payload = JSON.parse(options.body);
  if (config.turnstileSiteKey) {
    studyTradesTurnstileScript ||= new Promise((resolve, reject) => {
      if (window.turnstile) return resolve(window.turnstile);
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => { studyTradesTurnstileScript = null; reject(new Error("Security verification could not load. Please try again.")); };
      document.head.appendChild(script);
    });
    const turnstile = await studyTradesTurnstileScript;
    payload.captchaToken = await new Promise((resolve, reject) => {
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;bottom:20px;right:20px;z-index:2147483647";
      document.body.appendChild(container);
      let widget;
      const timer = window.setTimeout(() => finish(new Error("Security check expired. Please try again.")), 120000);
      function finish(error, token) {
        clearTimeout(timer);
        if (widget !== undefined) turnstile.remove(widget);
        container.remove();
        if (error) reject(error); else resolve(token);
      }
      widget = turnstile.render(container, { sitekey: config.turnstileSiteKey, action: "lead_capture",
        execution: "execute", appearance: "interaction-only",
        callback: (token) => finish(null, token),
        "error-callback": () => finish(new Error("Security check failed. Please try again.")),
        "expired-callback": () => finish(new Error("Security check expired. Please try again.")) });
      turnstile.execute(widget);
    });
  }
  return fetch(url, { ...options, body: JSON.stringify(payload) });
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll('input[type="email"]').forEach((input) => { input.maxLength = 254; });
  document.querySelectorAll('[data-signup-first-name], [data-signup-last-name], input[name="firstName"], input[name="lastName"]')
    .forEach((input) => { input.maxLength = 100; });
  initializeCookieConsent();
  initializeStudyTradesAnalytics();
  initializeHeaderScrollState();
  initializeMobileNav();
  initializeSiteLanguage();
  initializeGlobalWaitlistCapture();
  initializeLicenseSelectors();
  initializeSignupEntryLinks();
  initializeSignupBasicsPages();
  initializeSignupProfilePages();
  initializeSignupEligibilityPages();
  initializeSignupUnavailablePages();
  initializeSignupIneligiblePages();
  initializeSignupCheckoutPages();
  initializeWaitlistSignupPages();
  initializeProductConfirmationPages();
  initializeLicenseSignupGateways();
  initializeLeadCaptureForms();
  initializeFrankAssistant();
  initializeRequirementTooltips();
  initializeCourseCardOverlays();
  initializeAmbientBand();
  initializeAmbientSectionMotion();
  initializeSectionParallax();
  initializeTestimonialTouchPause();
  initializePracticeQuizDemos();
  initializeCaliforniaHubSelectors();
});

function trackStudyTradesEvent(eventName, parameters = {}) {
  if (!eventName) return;

  const eventParameters = Object.fromEntries(
    Object.entries(parameters).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

  if (window.Shopify?.analytics?.publish) {
    window.Shopify.analytics.publish(`study_trades:${eventName}`, eventParameters);
  }

  if (hasStudyTradesAnalyticsConsent() && typeof window.gtag === "function") {
    window.gtag("event", eventName, eventParameters);
  } else if (hasStudyTradesAnalyticsConsent()) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...eventParameters });
  }

  window.dispatchEvent(
    new CustomEvent("studytrades:analytics", {
      detail: { event: eventName, parameters: eventParameters }
    })
  );
}

window.studyTradesTrack = trackStudyTradesEvent;

function hasStudyTradesAnalyticsConsent() {
  try {
    return window.localStorage.getItem("studytrades:cookie-consent") === "accepted";
  } catch (error) {
    return false;
  }
}

function initializeCookieConsent() {
  const banner = document.querySelector("[data-cookie-banner]");
  if (!banner) return;

  const stored = window.localStorage.getItem("studytrades:cookie-consent");
  if (!stored) banner.hidden = false;

  banner.querySelector("[data-cookie-essential]")?.addEventListener("click", () => {
    window.localStorage.setItem("studytrades:cookie-consent", "essential");
    banner.hidden = true;
  });

  banner.querySelector("[data-cookie-accept]")?.addEventListener("click", () => {
    window.localStorage.setItem("studytrades:cookie-consent", "accepted");
    banner.hidden = true;
    trackStudyTradesEvent("cookie_consent_accept", { page_type: document.body.dataset.pageType });
  });
}

function initializeStudyTradesAnalytics() {
  const trackedScrollDepths = new Set();

  document.addEventListener("click", (event) => {
    const target = event.target.closest("[data-st-event], .seo-classification-tile, .seo-landing--b-general .seo-action-row .button--primary");
    if (!target) return;

    let eventName = target.dataset.stEvent;
    if (!eventName && target.matches(".seo-classification-tile")) eventName = "license_card_click";
    if (!eventName && target.closest(".seo-landing--b-general")) eventName = "start_license_prep_click";

    trackStudyTradesEvent(eventName, {
      button_text: target.dataset.stLabel || target.textContent.trim().replace(/\s+/g, " "),
      destination_url: target.getAttribute("href") || undefined,
      destination_anchor: target.getAttribute("href")?.startsWith("#") ? target.getAttribute("href") : undefined,
      page_type: target.dataset.stPageType || document.body.dataset.pageType,
      source_section: target.dataset.stSource || (target.matches(".seo-classification-tile") ? "popular_classifications" : undefined),
      license_code: target.dataset.stLicenseCode || target.querySelector(".seo-classification-tile__title")?.textContent.trim().split(" ")[0] || (target.closest(".seo-landing--b-general") ? "B" : undefined),
      license_name: target.dataset.stLicenseName || target.querySelector(".seo-classification-tile__title")?.textContent.trim() || (target.closest(".seo-landing--b-general") ? "B General Building" : undefined)
    });
  });

  document.addEventListener("toggle", (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    const summary = details.querySelector("summary")?.textContent.trim().replace(/\s+/g, " ");
    const guide = details.closest("[data-license-page]");
    const isBGeneralPage = Boolean(details.closest(".seo-landing--b-general"));
    if (details.matches(".faq-item")) {
      trackStudyTradesEvent("faq_expand", {
        faq_question: summary,
        license_code: guide?.dataset.licenseCode || (isBGeneralPage ? "B" : undefined),
        license_name: guide?.dataset.licenseName || (isBGeneralPage ? "B General Building" : undefined),
        page_type: guide ? "license_page" : document.body.dataset.pageType
      });
    } else if (details.closest(".classification-guide__question")) {
      const questions = Array.from(document.querySelectorAll(".classification-guide__question"));
      const question = details.closest(".classification-guide__question");
      trackStudyTradesEvent("sample_question_expand", {
        question_number: questions.indexOf(question) + 1,
        question_topic: question?.querySelector("h3")?.textContent.trim(),
        license_code: guide?.dataset.licenseCode,
        license_name: guide?.dataset.licenseName,
        page_type: "license_page"
      });
    }
  }, true);

  const recordScrollDepth = () => {
    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return;
    const percent = Math.round((window.scrollY / scrollableHeight) * 100);
    [25, 50, 75, 90].forEach((depth) => {
      if (percent >= depth && !trackedScrollDepths.has(depth)) {
        trackedScrollDepths.add(depth);
        trackStudyTradesEvent("scroll_depth", {
          percent_scrolled: depth,
          page_type: document.body.dataset.pageType
        });
      }
    });
  };

  window.addEventListener("scroll", recordScrollDepth, { passive: true });
}

function initializeCaliforniaHubSelectors() {
  document.querySelectorAll("[data-california-hub-selector]").forEach((root) => {
    const select = root.querySelector("[data-california-hub-license]");
    const startButton = root.querySelector("[data-california-hub-start]");
    if (!select || !startButton) return;

    select.addEventListener("change", () => {
      startButton.disabled = !select.value;
      const option = select.options[select.selectedIndex];
      trackStudyTradesEvent("license_selected", {
        state: "california",
        license_code: option?.dataset.licenseCode,
        license_name: option?.textContent.trim(),
        page_type: "california_hub"
      });
    });

    startButton.addEventListener("click", () => {
      if (!select.value) return;
      const option = select.options[select.selectedIndex];
      trackStudyTradesEvent("license_start_click", {
        state: "california",
        license_code: option?.dataset.licenseCode,
        license_name: option?.textContent.trim(),
        destination_url: select.value,
        page_type: "california_hub"
      });
      if (select.value.startsWith("#")) {
        document.querySelector(select.value)?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      window.location.href = select.value;
    });
  });
}

function initializePracticeQuizDemos() {
  document.querySelectorAll("[data-practice-quiz]").forEach((root) => {
    const questionData = root.querySelector("[data-quiz-questions]");
    const panel = root.querySelector("[data-quiz-panel]");
    const result = root.querySelector("[data-quiz-result]");
    const questionNode = root.querySelector("[data-quiz-question]");
    const optionsNode = root.querySelector("[data-quiz-options]");
    const progressNode = root.querySelector("[data-quiz-progress]");
    const progressBar = root.querySelector("[data-quiz-progress-bar]");
    const scoreNode = root.querySelector("[data-quiz-score]");
    const feedback = root.querySelector("[data-quiz-feedback]");
    const feedbackTitle = root.querySelector("[data-quiz-feedback-title]");
    const explanationNode = root.querySelector("[data-quiz-explanation]");
    const checkButton = root.querySelector("[data-quiz-check]");
    const nextButton = root.querySelector("[data-quiz-next]");
    const restartButton = root.querySelector("[data-quiz-restart]");
    const resultTitle = root.querySelector("[data-quiz-result-title]");

    if (!questionData || !panel || !result || !questionNode || !optionsNode || !checkButton || !nextButton) return;

    let questionBank = [];
    try {
      questionBank = JSON.parse(questionData.textContent || "[]");
    } catch (_error) {
      return;
    }

    const requestedCount = Number.parseInt(root.getAttribute("data-question-count") || "5", 10);
    const questionCount = Math.min(Math.max(requestedCount, 1), questionBank.length);
    let questions = [];
    let currentIndex = 0;
    let selectedIndex = null;
    let score = 0;
    let graded = false;

    function shuffledQuestions() {
      const shuffled = [...questionBank];
      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
      }
      return shuffled.slice(0, questionCount);
    }

    function selectAnswer(index) {
      if (graded) return;
      selectedIndex = index;
      optionsNode.querySelectorAll("button").forEach((button, buttonIndex) => {
        const isSelected = buttonIndex === index;
        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-pressed", String(isSelected));
      });
      checkButton.disabled = false;
      checkButton.hidden = false;
    }

    function renderQuestion() {
      const question = questions[currentIndex];
      selectedIndex = null;
      graded = false;
      questionNode.textContent = question.question;
      optionsNode.replaceChildren();
      feedback.hidden = true;
      feedback.classList.remove("is-correct", "is-incorrect");
      checkButton.hidden = true;
      checkButton.disabled = true;
      nextButton.hidden = true;

      question.options.forEach((option, index) => {
        const button = document.createElement("button");
        const letter = document.createElement("span");
        const copy = document.createElement("span");
        button.type = "button";
        button.className = "practice-quiz-demo__option";
        button.setAttribute("aria-pressed", "false");
        letter.className = "practice-quiz-demo__option-letter";
        letter.textContent = String.fromCharCode(65 + index);
        copy.textContent = option;
        button.append(letter, copy);
        button.addEventListener("click", () => selectAnswer(index));
        optionsNode.append(button);
      });

      if (progressNode) progressNode.textContent = `Question ${currentIndex + 1} of ${questions.length}`;
      if (scoreNode) scoreNode.textContent = `${score} correct`;
      if (progressBar) progressBar.style.width = `${((currentIndex + 1) / questions.length) * 100}%`;
    }

    function gradeAnswer() {
      if (selectedIndex === null || graded) return;
      graded = true;
      const question = questions[currentIndex];
      const isCorrect = selectedIndex === question.answer;
      if (isCorrect) score += 1;

      optionsNode.querySelectorAll("button").forEach((button, index) => {
        button.disabled = true;
        button.classList.toggle("is-correct", index === question.answer);
        button.classList.toggle("is-incorrect", index === selectedIndex && !isCorrect);
      });

      feedback.hidden = false;
      feedback.classList.add(isCorrect ? "is-correct" : "is-incorrect");
      if (feedbackTitle) feedbackTitle.textContent = isCorrect ? "Correct." : "Not quite.";
      if (explanationNode) explanationNode.textContent = question.explanation;
      if (scoreNode) scoreNode.textContent = `${score} correct`;
      checkButton.hidden = true;
      nextButton.hidden = false;
      nextButton.textContent = currentIndex === questions.length - 1 ? "See my score" : "Next question";
    }

    function showResult() {
      panel.hidden = true;
      result.hidden = false;
      if (resultTitle) resultTitle.textContent = `${score} out of ${questions.length} correct`;
    }

    function startQuiz() {
      questions = shuffledQuestions();
      currentIndex = 0;
      selectedIndex = null;
      score = 0;
      graded = false;
      panel.hidden = false;
      result.hidden = true;
      renderQuestion();
    }

    checkButton.addEventListener("click", gradeAnswer);
    nextButton.addEventListener("click", () => {
      if (currentIndex >= questions.length - 1) {
        showResult();
        return;
      }
      currentIndex += 1;
      renderQuestion();
    });
    restartButton?.addEventListener("click", startQuiz);
    startQuiz();
  });
}

const STUDY_TRADES_STATE_OPTIONS = [
  { code: "AL", label: "Alabama" },
  { code: "AK", label: "Alaska" },
  { code: "AZ", label: "Arizona" },
  { code: "AR", label: "Arkansas" },
  { code: "CA", label: "California" },
  { code: "CO", label: "Colorado" },
  { code: "CT", label: "Connecticut" },
  { code: "DE", label: "Delaware" },
  { code: "FL", label: "Florida" },
  { code: "GA", label: "Georgia" },
  { code: "HI", label: "Hawaii" },
  { code: "ID", label: "Idaho" },
  { code: "IL", label: "Illinois" },
  { code: "IN", label: "Indiana" },
  { code: "IA", label: "Iowa" },
  { code: "KS", label: "Kansas" },
  { code: "KY", label: "Kentucky" },
  { code: "LA", label: "Louisiana" },
  { code: "ME", label: "Maine" },
  { code: "MD", label: "Maryland" },
  { code: "MA", label: "Massachusetts" },
  { code: "MI", label: "Michigan" },
  { code: "MN", label: "Minnesota" },
  { code: "MS", label: "Mississippi" },
  { code: "MO", label: "Missouri" },
  { code: "MT", label: "Montana" },
  { code: "NE", label: "Nebraska" },
  { code: "NV", label: "Nevada" },
  { code: "NH", label: "New Hampshire" },
  { code: "NJ", label: "New Jersey" },
  { code: "NM", label: "New Mexico" },
  { code: "NY", label: "New York" },
  { code: "NC", label: "North Carolina" },
  { code: "ND", label: "North Dakota" },
  { code: "OH", label: "Ohio" },
  { code: "OK", label: "Oklahoma" },
  { code: "OR", label: "Oregon" },
  { code: "PA", label: "Pennsylvania" },
  { code: "RI", label: "Rhode Island" },
  { code: "SC", label: "South Carolina" },
  { code: "SD", label: "South Dakota" },
  { code: "TN", label: "Tennessee" },
  { code: "TX", label: "Texas" },
  { code: "UT", label: "Utah" },
  { code: "VT", label: "Vermont" },
  { code: "VA", label: "Virginia" },
  { code: "WA", label: "Washington" },
  { code: "WV", label: "West Virginia" },
  { code: "WI", label: "Wisconsin" },
  { code: "WY", label: "Wyoming" }
];

const CALIFORNIA_LICENSE_OPTIONS = [
  { slug: "law-business", label: "Law and Business" },
  { slug: "a-general-engineering-contractor", label: "A General Engineering" },
  { slug: "b-general-building-contractor", label: "B General Building" },
  { slug: "b-2-residential-remodeling-contractor", label: "B-2 Residential Remodeling" },
  { slug: "c-2-insulation-and-acoustical-contractor", label: "C-2 Insulation and Acoustical" },
  { slug: "c-4-boiler-hot-water-heating-and-steam-fitting-contractor", label: "C-4 Boiler, Hot-Water Heating and Steam Fitting" },
  { slug: "c-5-framing-and-rough-carpentry-contractor", label: "C-5 Framing and Rough Carpentry" },
  { slug: "c-6-cabinet-millwork-and-finish-carpentry-contractor", label: "C-6 Cabinet, Millwork and Finish Carpentry" },
  { slug: "c-7-low-voltage-systems-contractor", label: "C-7 Low Voltage Systems" },
  { slug: "c-8-concrete-contractor", label: "C-8 Concrete" },
  { slug: "c-9-drywall-contractor", label: "C-9 Drywall" },
  { slug: "c-10-electrical-contractor", label: "C-10 Electrical" },
  { slug: "c-11-elevator-contractor", label: "C-11 Elevator" },
  { slug: "c-12-earthwork-and-paving-contractor", label: "C-12 Earthwork and Paving" },
  { slug: "c-13-fencing-contractor", label: "C-13 Fencing" },
  { slug: "c-15-flooring-and-floor-covering-contractor", label: "C-15 Flooring and Floor Covering" },
  { slug: "c-16-fire-protection-contractor", label: "C-16 Fire Protection" },
  { slug: "c-17-glazing-contractor", label: "C-17 Glazing" },
  { slug: "c-20-warm-air-heating-ventilating-and-air-conditioning-contractor", label: "C-20 Warm-Air Heating, Ventilating and Air-Conditioning" },
  { slug: "c-21-building-moving-demolition-contractor", label: "C-21 Building Moving/Demolition" },
  { slug: "c-22-asbestos-abatement-contractor", label: "C-22 Asbestos Abatement" },
  { slug: "c-23-ornamental-metal-contractor", label: "C-23 Ornamental Metal" },
  { slug: "c-27-landscaping-contractor", label: "C-27 Landscaping" },
  { slug: "c-28-lock-and-security-equipment-contractor", label: "C-28 Lock and Security Equipment" },
  { slug: "c-29-masonry-contractor", label: "C-29 Masonry" },
  { slug: "c-31-construction-zone-traffic-control-contractor", label: "C-31 Construction Zone Traffic Control" },
  { slug: "c-32-parking-and-highway-improvement-contractor", label: "C-32 Parking and Highway Improvement" },
  { slug: "c-33-painting-and-decorating-contractor", label: "C-33 Painting and Decorating" },
  { slug: "c-34-pipeline-contractor", label: "C-34 Pipeline" },
  { slug: "c-35-lathing-and-plastering-contractor", label: "C-35 Lathing and Plastering" },
  { slug: "c-36-plumbing-contractor", label: "C-36 Plumbing" },
  { slug: "c-38-refrigeration-contractor", label: "C-38 Refrigeration" },
  { slug: "c-39-roofing-contractor", label: "C-39 Roofing" },
  { slug: "c-42-sanitation-system-contractor", label: "C-42 Sanitation System" },
  { slug: "c-43-sheet-metal-contractor", label: "C-43 Sheet Metal" },
  { slug: "c-45-sign-contractor", label: "C-45 Sign" },
  { slug: "c-46-solar-contractor", label: "C-46 Solar" },
  { slug: "c-47-general-manufactured-housing-contractor", label: "C-47 General Manufactured Housing" },
  { slug: "c-49-tree-and-palm-contractor", label: "C-49 Tree and Palm" },
  { slug: "c-50-reinforcing-steel-contractor", label: "C-50 Reinforcing Steel" },
  { slug: "c-51-structural-steel-contractor", label: "C-51 Structural Steel" },
  { slug: "c-53-swimming-pool-contractor", label: "C-53 Swimming Pool" },
  { slug: "c-54-ceramic-and-mosaic-tile-contractor", label: "C-54 Ceramic and Mosaic Tile" },
  { slug: "c-55-water-conditioning-contractor", label: "C-55 Water Conditioning" },
  { slug: "c-57-well-drilling-contractor", label: "C-57 Well Drilling" },
  { slug: "c-60-welding-contractor", label: "C-60 Welding" },
  { slug: "c-61-limited-specialty-contractor", label: "C-61 Limited Specialty" }
];

const STUDY_TRADES_FALLBACK_LICENSES = {
  CA: CALIFORNIA_LICENSE_OPTIONS,
  WA: [
    { slug: "general-contractor", label: "General Contractor" }
  ]
};

const DEFAULT_FALLBACK_LICENSES = [
  { slug: "general-contractor", label: "General Contractor" }
];

const STUDY_TRADES_LAUNCH_SETTINGS_URL = "https://app.studytrades.com/api/public/license-launch-settings";
const STUDY_TRADES_PRIMARY_STATE = "CA";
const STUDY_TRADES_PRIMARY_LICENSE_SLUG = "b-general-building-contractor";
const STUDY_TRADES_PRIMARY_LICENSE_LABEL = "B General Building";
const CSLB_REQUIREMENTS_URL = "https://www.cslb.ca.gov/Contractors/Applicants/Contractors_License/Exam_Application/Before_Applying_For_License.aspx";
const STUDY_TRADES_UNSUPPORTED_SHOPIFY_PRODUCT_SLUGS = new Set(["c-61-limited-specialty-contractor"]);
const STUDY_TRADES_DEFAULT_LAUNCHED_SLUGS = new Set(
  CALIFORNIA_LICENSE_OPTIONS
    .map((license) => license.slug)
    .filter((slug) => !STUDY_TRADES_UNSUPPORTED_SHOPIFY_PRODUCT_SLUGS.has(slug))
);

function productUrlForSelection(stateCode, licenseSlug) {
  if (stateCode !== "CA" || !licenseSlug) return "";
  if (licenseSlug === "law-business") return "/products/california-law-and-business";
  return `/products/california-${licenseSlug.replace(/-contractor$/, "")}`;
}

function licenseSlugFromProductHandle(handle) {
  if (handle === "california-law-and-business") return "law-business";
  const productUrl = `/products/${handle}`;
  return CALIFORNIA_LICENSE_OPTIONS.find((license) => productUrlForSelection("CA", license.slug) === productUrl)?.slug || "";
}

function licenseSlugFromClassificationPath(pathname) {
  const match = String(pathname || "").match(/\/pages\/california-(.+)-license\/?$/);
  if (!match) return "";
  const slug = match[1];
  return CALIFORNIA_LICENSE_OPTIONS.some((license) => license.slug === slug) ? slug : "";
}

function licenseFromSignupReferrer() {
  try {
    const referrer = new URL(document.referrer);
    if (referrer.origin !== window.location.origin) return null;
    const slug = licenseSlugFromClassificationPath(referrer.pathname);
    if (!slug) return null;
    const license = CALIFORNIA_LICENSE_OPTIONS.find((item) => item.slug === slug);
    return license ? { slug: license.slug, label: license.label } : null;
  } catch (_error) {
    return null;
  }
}

function createDefaultStudyTradesOffers() {
  return [
    ...CALIFORNIA_LICENSE_OPTIONS.map((license) => ({
      stateCode: "CA",
      stateLabel: "California",
      licenseSlug: license.slug,
      licenseLabel: license.label,
      productUrl: productUrlForSelection("CA", license.slug),
      available: STUDY_TRADES_DEFAULT_LAUNCHED_SLUGS.has(license.slug),
      status: STUDY_TRADES_DEFAULT_LAUNCHED_SLUGS.has(license.slug) ? "live" : "waitlist",
      allowCheckout: STUDY_TRADES_DEFAULT_LAUNCHED_SLUGS.has(license.slug),
      allowAppAccess: STUDY_TRADES_DEFAULT_LAUNCHED_SLUGS.has(license.slug),
      isVisible: true,
      priceLabel: "$250"
    })),
    {
      stateCode: "WA",
      stateLabel: "Washington",
      licenseSlug: "general-contractor",
      licenseLabel: "General Contractor",
      productUrl: "",
      available: false,
      status: "waitlist",
      allowCheckout: false,
      allowAppAccess: false,
      isVisible: true,
      priceLabel: "$250"
    }
  ];
}

let studyTradesOffersCache = createDefaultStudyTradesOffers();
let studyTradesOffersPromise = null;
let studyTradesOffersLoadedAt = 0;
const STUDY_TRADES_OFFERS_TTL_MS = 30 * 1000;

function mergeOffersWithLaunchSettings(offers, launchSettings) {
  const launchMap = new Map(
    (launchSettings || []).map((item) => [item.licenseSlug, item])
  );

  return offers.map((offer) => {
    if (offer.stateCode !== "CA") return { ...offer, available: Boolean(offer.available), status: "waitlist", allowCheckout: false, isVisible: true };
    const setting = launchMap.get(offer.licenseSlug);
    const status = setting?.status || (setting?.isLaunched && setting?.allowSelfEnrollment ? "live" : "waitlist");
    return {
      ...offer,
      productUrl: offer.productUrl || productUrlForSelection(offer.stateCode, offer.licenseSlug),
      status,
      available: status === "live",
      allowCheckout: Boolean(setting?.allowCheckout ?? (status === "live" || status === "preorder")),
      allowAppAccess: Boolean(setting?.allowAppAccess ?? status === "live"),
      isVisible: Boolean(setting?.isVisible ?? status !== "hidden")
    };
  }).filter((offer) => offer.isVisible !== false);
}

function mergeOffersWithOfferAvailability(offers, availabilityOffers) {
  const availabilityMap = new Map(
    (availabilityOffers || []).map((item) => [item.stateCode && item.licenseSlug ? `${item.stateCode}:${item.licenseSlug}` : "", item])
  );

  return offers.map((offer) => {
    const key = `${offer.stateCode}:${offer.licenseSlug}`;
    const availability = availabilityMap.get(key);
    return {
      ...offer,
      status: availability?.status || offer.status || (offer.available ? "live" : "waitlist"),
      available: availabilityMap.has(key) ? Boolean(availability.available) : Boolean(offer.available),
      allowCheckout: availabilityMap.has(key) ? Boolean(availability.allowCheckout) : Boolean(offer.allowCheckout),
      allowAppAccess: availabilityMap.has(key) ? Boolean(availability.allowAppAccess) : Boolean(offer.allowAppAccess),
      isVisible: availabilityMap.has(key) ? availability.isVisible !== false : offer.isVisible !== false,
      productUrl: offer.productUrl || productUrlForSelection(offer.stateCode, offer.licenseSlug)
    };
  }).filter((offer) => offer.isVisible !== false);
}

async function loadStudyTradesOffers(options = {}) {
  const force = Boolean(options.force);
  const now = Date.now();
  if (!force && studyTradesOffersPromise && now - studyTradesOffersLoadedAt < STUDY_TRADES_OFFERS_TTL_MS) {
    return studyTradesOffersPromise;
  }

  studyTradesOffersPromise = fetch(STUDY_TRADES_LAUNCH_SETTINGS_URL, {
    headers: { Accept: "application/json" },
    cache: "no-store"
  })
    .then(async (response) => {
      if (!response.ok) throw new Error("Launch settings request failed");
      return response.json();
    })
    .then((launchSettings) => {
      studyTradesOffersCache = mergeOffersWithLaunchSettings(createDefaultStudyTradesOffers(), launchSettings);
      studyTradesOffersLoadedAt = Date.now();
      return studyTradesOffersCache;
    })
    .catch(() => {
      studyTradesOffersLoadedAt = Date.now();
      return studyTradesOffersCache;
    });

  return studyTradesOffersPromise;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function findStateLabel(stateCode) {
  return STUDY_TRADES_STATE_OPTIONS.find((option) => option.code === stateCode)?.label || stateCode;
}

function findOfferForSelection(offers, stateCode, licenseSlug) {
  return offers.find((offer) => offer.stateCode === stateCode && offer.licenseSlug === licenseSlug) || null;
}

function isLaunchedSignupSelection(stateCode, licenseSlug) {
  return stateCode === STUDY_TRADES_PRIMARY_STATE && STUDY_TRADES_DEFAULT_LAUNCHED_SLUGS.has(licenseSlug);
}

function offerCanCheckout(offer) {
  return Boolean(offer?.allowCheckout || offer?.available);
}

function offerIsPreorder(offer) {
  return offer?.status === "preorder";
}

function licenseIsPreorder(licenseSlug, offers = studyTradesOffersCache) {
  return offers.some((offer) => offer.stateCode === "CA" && offer.licenseSlug === licenseSlug && offer.status === "preorder");
}

function dedupeLicenses(licenses) {
  const seen = new Set();
  return licenses.filter((license) => {
    if (!license.slug || seen.has(license.slug)) return false;
    seen.add(license.slug);
    return true;
  });
}

function getLicenseChoicesForState(stateCode, offers) {
  const offeredLicenses = dedupeLicenses(
    offers
      .filter((offer) => offer.stateCode === stateCode)
      .map((offer) => ({
        slug: offer.licenseSlug,
        label: offer.licenseLabel
      }))
  );

  if (offeredLicenses.length) return offeredLicenses;
  return STUDY_TRADES_FALLBACK_LICENSES[stateCode] || DEFAULT_FALLBACK_LICENSES;
}

function populateStateOptions(select, selectedStateCode = "CA") {
  if (!select) return;
  select.innerHTML = STUDY_TRADES_STATE_OPTIONS.map((option) => `
    <option value="${option.code}">${option.label}</option>
  `).join("");
  select.value = STUDY_TRADES_STATE_OPTIONS.some((option) => option.code === selectedStateCode) ? selectedStateCode : "CA";
}

function populateLicenseOptions(select, choices, selectedSlug = "", placeholder = "Select your license") {
  if (!select) return;
  select.innerHTML = `
    <option value="">${placeholder}</option>
    ${choices.map((choice) => `
      <option value="${escapeHtml(choice.slug)}">${escapeHtml(choice.label)}</option>
    `).join("")}
  `;

  if (choices.some((choice) => choice.slug === selectedSlug)) {
    select.value = selectedSlug;
  } else {
    select.value = "";
  }
}

function readStoredSignupLead() {
  try {
    return JSON.parse(window.localStorage.getItem("studyTradesSignupLicense") || "{}");
  } catch (_error) {
    return {};
  }
}

function writeStoredSignupLead(values) {
  const existing = readStoredSignupLead();
  const next = { ...existing, ...values };
  try {
    window.localStorage.setItem("studyTradesSignupLicense", JSON.stringify(next));
    if (next.email) {
      window.localStorage.setItem("studyTradesSignupEmail", next.email);
    }
  } catch (_error) {
    // Ignore local storage failures.
  }
  return next;
}

function buildSignupSelection(params = new URLSearchParams(window.location.search), overrides = {}) {
  const stored = readStoredSignupLead();
  const stateCode = overrides.stateCode || params.get("stateCode") || stored.stateCode || "CA";
  const stateLabel = overrides.stateLabel || params.get("stateLabel") || stored.stateLabel || findStateLabel(stateCode);
  const licenseSlug = overrides.licenseSlug || params.get("licenseSlug") || stored.licenseSlug || "";
  const licenseChoices = getLicenseChoicesForState(stateCode, studyTradesOffersCache);
  const fallbackLabel = licenseChoices.find((choice) => choice.slug === licenseSlug)?.label || "";
  const licenseLabel = overrides.licenseLabel || params.get("licenseLabel") || stored.licenseLabel || fallbackLabel;
  const offer = findOfferForSelection(studyTradesOffersCache, stateCode, licenseSlug);
  const status = overrides.status || params.get("status") || stored.status || offer?.status || (offer?.available ? "live" : "waitlist");
  const allowCheckout = Boolean(
    overrides.allowCheckout ?? (params.get("allowCheckout") === "1" || stored.allowCheckout || offerCanCheckout(offer))
  );

  return {
    firstName: overrides.firstName || params.get("firstName") || stored.firstName || "",
    lastName: overrides.lastName || params.get("lastName") || stored.lastName || "",
    phone: overrides.phone || params.get("phone") || stored.phone || "",
    email: overrides.email || params.get("email") || stored.email || window.localStorage.getItem("studyTradesSignupEmail") || "",
    stateCode,
    stateLabel,
    licenseSlug,
    licenseLabel,
    requestedLabel: licenseLabel ? `${stateLabel} · ${licenseLabel}` : stateLabel,
    status,
    available: status === "live",
    allowCheckout,
    productUrl: offer?.productUrl || params.get("productUrl") || stored.productUrl || productUrlForSelection(stateCode, licenseSlug),
    priceLabel: offer?.priceLabel || params.get("priceLabel") || stored.priceLabel || ""
  };
}

function buildSignupQuery(selection) {
  const params = new URLSearchParams({
    stateCode: selection.stateCode,
    stateLabel: selection.stateLabel
  });

  if (selection.licenseSlug) params.set("licenseSlug", selection.licenseSlug);
  if (selection.licenseLabel) params.set("licenseLabel", selection.licenseLabel);
  if (selection.status) params.set("status", selection.status);
  if (selection.allowCheckout) params.set("allowCheckout", "1");
  if (selection.productUrl) params.set("productUrl", selection.productUrl);
  if (selection.priceLabel) params.set("priceLabel", selection.priceLabel);
  return params.toString();
}

function moneyFromCents(cents) {
  if (typeof cents !== "number" || Number.isNaN(cents)) return "$0";
  return `$${(cents / 100).toFixed(2).replace(/\.00$/, "")}`;
}

function productJsonUrl(productUrl) {
  const clean = (productUrl || "").replace(/\/$/, "");
  return clean ? `${clean}.js` : "";
}

async function fetchShopifyProduct(productUrl) {
  const response = await fetch(productJsonUrl(productUrl), {
    headers: { Accept: "application/json" }
  });
  if (!response.ok) throw new Error("Product lookup failed");
  return response.json();
}

function firstAvailableVariant(product) {
  return Array.isArray(product?.variants)
    ? product.variants.find((variant) => variant.available) || product.variants[0] || null
    : null;
}

function checkoutPrefillQuery(selection) {
  const params = new URLSearchParams();
  if (selection.email) params.set("checkout[email]", selection.email);
  if (selection.firstName) params.set("checkout[shipping_address][first_name]", selection.firstName);
  if (selection.lastName) params.set("checkout[shipping_address][last_name]", selection.lastName);
  if (selection.phone) params.set("checkout[shipping_address][phone]", selection.phone);
  return params.toString();
}

async function redirectSelectionToShopifyCheckout(selection, options = {}) {
  const fallbackUrl = options.fallbackUrl || selection.productUrl || "/#license-selector";
  const productUrl = selection.productUrl || productUrlForSelection(selection.stateCode, selection.licenseSlug);
  if (!productUrl) {
    window.location.href = fallbackUrl;
    return;
  }

  try {
    const product = await fetchShopifyProduct(productUrl);
    const variant = firstAvailableVariant(product);
    if (!variant?.id) throw new Error("No available variant");

    await fetch("/cart/clear.js", {
      method: "POST",
      headers: { Accept: "application/json" }
    });

    const prefill = checkoutPrefillQuery(selection);
    window.location.href = prefill ? `/cart/${variant.id}:1?${prefill}` : `/cart/${variant.id}:1`;
  } catch (_error) {
    window.location.href = fallbackUrl;
  }
}

function initializeProductConfirmationPages() {
  const pages = document.querySelectorAll("[data-product-confirmation]");
  if (!pages.length) return;

  pages.forEach((root) => {
    const form = root.querySelector("form");
    const variantSelect = root.querySelector("[data-product-variant-select]");
    const addonSelect = root.querySelector("[data-product-addon-select]");
    const addonList = root.querySelector("[data-product-addon-list]");
    const totalNode = root.querySelector("[data-product-total]");
    const primaryRemoveButton = root.querySelector("[data-product-primary-remove]");
    const primaryPriceNode = root.querySelector("[data-product-primary-price]");
    const modal = root.closest(".product-checkout")?.querySelector("[data-product-included-modal]");
    const openIncludedButton = root.querySelector("[data-product-included-open]");
    if (!form || !totalNode) return;

    const money = (cents) => `$${(cents / 100).toFixed(2)}`;
    const currentProductUrl = `/products/${root.getAttribute("data-product-handle") || ""}`;
    const primaryLicenseSlug = licenseSlugFromProductHandle(root.getAttribute("data-product-handle") || "");
    const selectedAddons = [];
    let launchOffers = studyTradesOffersCache;
    let primarySelected = true;
    const selectedVariantId = () => variantSelect?.value || root.getAttribute("data-variant-id") || "";
    const selectedPrice = () => {
      const selectedOption = variantSelect?.options[variantSelect.selectedIndex];
      return Number(selectedOption?.getAttribute("data-price") || root.getAttribute("data-variant-price") || 0);
    };
    const addonOptions = CALIFORNIA_LICENSE_OPTIONS
      .filter((license) => license.slug !== "law-business")
      .filter((license) => !STUDY_TRADES_UNSUPPORTED_SHOPIFY_PRODUCT_SLUGS.has(license.slug))
      .map((license) => ({
        ...license,
        productUrl: productUrlForSelection("CA", license.slug)
      }))
      .filter((license) => license.productUrl && license.productUrl !== currentProductUrl);

    const populateAddonOptions = () => {
      if (!addonSelect) return;
      addonSelect.innerHTML = [
        '<option value="">+ Another License Study Guide</option>',
        ...addonOptions.map((license) => `<option value="${license.slug}">${escapeHtml(license.label)}</option>`)
      ].join("");
    };

    const displayPrice = (cents, isPreorder) => (isPreorder ? Math.round(cents * 0.5) : cents);
    const priceMarkup = (cents, isPreorder) => {
      if (!isPreorder) return escapeHtml(money(cents));
      return `
        <span class="product-checkout__price-stack">
          <s>${escapeHtml(money(cents))}</s>
          <strong>${escapeHtml(money(displayPrice(cents, true)))} <em>(50% off)</em></strong>
        </span>
      `;
    };
    const syncPrimaryPrice = () => {
      if (!primaryPriceNode) return;
      primaryPriceNode.innerHTML = priceMarkup(selectedPrice(), licenseIsPreorder(primaryLicenseSlug, launchOffers));
    };
    const syncTotal = () => {
      const primaryTotal = primarySelected ? displayPrice(selectedPrice(), licenseIsPreorder(primaryLicenseSlug, launchOffers)) : 0;
      const addonTotal = selectedAddons.reduce((sum, addon) => sum + displayPrice(addon.price, addon.isPreorder), 0);
      totalNode.textContent = money(primaryTotal + addonTotal);
    };

    const renderAddons = () => {
      if (!addonList) return;
      addonList.innerHTML = selectedAddons.map((addon) => `
        <div class="product-checkout__course-row product-checkout__addon" data-addon-slug="${escapeHtml(addon.slug)}">
          <span>${escapeHtml(addon.label)} Study Guide + Exam Prep</span>
          <span>${priceMarkup(addon.price, addon.isPreorder)}</span>
          <button type="button" data-product-addon-remove="${escapeHtml(addon.slug)}" aria-label="Remove ${escapeHtml(addon.label)}">
            <span class="material-symbols-outlined" aria-hidden="true">delete</span>
          </button>
        </div>
      `).join("");
      syncPrimaryPrice();
      syncTotal();
    };

    if (variantSelect) variantSelect.addEventListener("change", () => {
      syncPrimaryPrice();
      syncTotal();
    });
    if (primaryRemoveButton) {
      primaryRemoveButton.addEventListener("click", () => {
        primarySelected = false;
        primaryRemoveButton.closest(".product-checkout__course-row")?.remove();
        syncTotal();
      });
    }
    if (openIncludedButton && modal) {
      const closeButtons = modal.querySelectorAll("[data-product-included-close]");
      const openModal = () => {
        modal.hidden = false;
        document.body.classList.add("product-modal-open");
      };
      const closeModal = () => {
        modal.hidden = true;
        document.body.classList.remove("product-modal-open");
      };
      openIncludedButton.addEventListener("click", openModal);
      closeButtons.forEach((button) => button.addEventListener("click", closeModal));
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && !modal.hidden) closeModal();
      });
    }
    if (addonSelect) {
      addonSelect.addEventListener("change", async () => {
        const slug = addonSelect.value;
        const option = addonOptions.find((item) => item.slug === slug);
        if (!option || selectedAddons.some((addon) => addon.slug === slug)) {
          addonSelect.value = "";
          return;
        }

        addonSelect.disabled = true;
        try {
          const product = await fetchShopifyProduct(option.productUrl);
          const variant = firstAvailableVariant(product);
          if (!variant?.id) throw new Error("No available variant");
          selectedAddons.push({
            slug: option.slug,
            label: option.label,
            productUrl: option.productUrl,
            variantId: variant.id,
            price: Number(variant.price) || 0,
            isPreorder: licenseIsPreorder(option.slug, launchOffers)
          });
          addonSelect.value = "";
          renderAddons();
        } catch (_error) {
          addonSelect.value = "";
        } finally {
          addonSelect.disabled = false;
        }
      });
    }
    if (addonList) {
      addonList.addEventListener("click", (event) => {
        const button = event.target.closest("[data-product-addon-remove]");
        if (!button) return;
        const slug = button.getAttribute("data-product-addon-remove");
        const index = selectedAddons.findIndex((addon) => addon.slug === slug);
        if (index >= 0) selectedAddons.splice(index, 1);
        renderAddons();
      });
    }

    form.addEventListener("submit", async (event) => {
      const variantId = selectedVariantId();
      if (!variantId) return;
      event.preventDefault();
      const lines = [
        ...(primarySelected ? [`${variantId}:1`] : []),
        ...selectedAddons.map((addon) => `${addon.variantId}:1`)
      ];
      if (!lines.length) return;

      try {
        await fetch("/cart/clear.js", {
          method: "POST",
          headers: { Accept: "application/json" }
        });
      } catch (_error) {
        // The cart permalink below still creates a deterministic checkout target.
      }

      const prefill = checkoutPrefillQuery(buildSignupSelection());
      window.location.href = prefill ? `/cart/${lines.join(",")}?${prefill}` : `/cart/${lines.join(",")}`;
    });

    populateAddonOptions();
    loadStudyTradesOffers().then((offers) => {
      launchOffers = offers;
      selectedAddons.forEach((addon) => {
        addon.isPreorder = licenseIsPreorder(addon.slug, launchOffers);
      });
      renderAddons();
    }).catch(() => {
      syncPrimaryPrice();
      syncTotal();
    });
    syncPrimaryPrice();
    syncTotal();
  });
}

function initializeHeaderScrollState() {
  const header = document.querySelector(".site-header");
  if (!header || !document.body.classList.contains("template-index")) return;
  const alertBanner = document.querySelector(".site-alert-banner");

  const syncHeader = () => {
    if (alertBanner) {
      document.documentElement.style.setProperty("--site-alert-height", `${alertBanner.offsetHeight}px`);
    }
    header.classList.toggle("is-scrolled", window.scrollY > 16);
  };

  syncHeader();
  window.addEventListener("scroll", syncHeader, { passive: true });
  window.addEventListener("resize", syncHeader, { passive: true });
}

function initializeMobileNav() {
  const toggle = document.querySelector(".site-nav-toggle");
  const nav = document.querySelector(".site-drawer-nav");
  const header = document.querySelector(".site-header");
  const backdrop = document.querySelector(".site-nav-backdrop");
  if (!toggle || !nav || !header) return;

  const closeNav = () => {
    toggle.setAttribute("aria-expanded", "false");
    nav.classList.remove("is-open");
    header.classList.remove("is-menu-open");
    document.body.classList.remove("mobile-nav-open");
  };

  const openNav = () => {
    toggle.setAttribute("aria-expanded", "true");
    nav.classList.add("is-open");
    header.classList.add("is-menu-open");
    document.body.classList.add("mobile-nav-open");
  };

  toggle.addEventListener("click", () => {
    const isOpen = toggle.getAttribute("aria-expanded") === "true";
    if (isOpen) closeNav();
    else openNav();
  });

  if (backdrop) {
    backdrop.addEventListener("click", () => closeNav());
  }

  nav.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => closeNav());
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeNav();
  });

  let touchStartX = 0;
  let touchStartY = 0;

  document.addEventListener("touchstart", (event) => {
    const touch = event.touches[0];
    if (!touch) return;
    touchStartX = touch.clientX;
    touchStartY = touch.clientY;
  }, { passive: true });

  document.addEventListener("touchend", (event) => {
    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchStartX;
    const deltaY = Math.abs(touch.clientY - touchStartY);
    const isOpen = toggle.getAttribute("aria-expanded") === "true";
    const isMobileViewport = window.matchMedia("(max-width: 900px)").matches;

    if (!isOpen && isMobileViewport && touchStartX <= 28 && deltaX > 48 && deltaY < 40) {
      openNav();
    }
  }, { passive: true });
}

function initializeSiteLanguage() {
  const languageSelects = Array.from(document.querySelectorAll("[data-language-select]"));
  if (!languageSelects.length) return;

  const languageFlags = {
    en: "🇺🇸",
    es: "🇲🇽"
  };

  const translations = {
    en: {
      i18n: {
        brand_name: "Study Trades",
        nav_how: "How it Works",
        nav_plans: "Plans",
        nav_faq: "FAQ",
        nav_contact: "Contact",
        nav_cta: "Start Your Test Prep",
        nav_cta_mobile: "Start Prep",
        footer_brand: "Study Trades",
        footer_intro: "Practical contractor licensing guidance, exam prep, and study tools for working adults.",
        footer_col1: "Get Started",
        footer_how: "How it Works",
        footer_plans: "Plans",
        footer_waitlist: "Join Waitlist",
        footer_col2: "Support",
        footer_faq: "FAQ",
        footer_contact: "Contact",
        footer_about: "About",
        footer_student_support: "Student Support",
        footer_col3: "Policies",
        footer_privacy: "Privacy Policy",
        footer_terms: "Terms",
        footer_refund: "Refund Policy",
        footer_accessibility: "Accessibility",
        footer_col4: "Contact",
        footer_hours: "Mon-Fri, 8am-5pm PT",
        footer_login: "Learner login"
      },
      heroLines: ["Get Licensed,", "Zero Guesswork."],
      heroSubheading: "Study Trades helps future and current contractors understand their license path, prepare for their exams, and stay organized from application to test day.",
      selectorDefault: "Select your license",
      selectorButton: "Start Your Test Prep",
      processLabels: ["1", "2", "3"],
      processTitles: ["Learn from anywhere, anytime.", "Study your trade", "Pass your test"],
      processBodies: [
        "Read or listen to lessons specific to your trade classification at any time of day, no in-person class.",
        "As you progress through the material, take quizzes and test your knowledge with practice exams that mirror the real thing. We give you instant scorecards that will tell you when you are ready.",
        "Our practice exams are based on the real thing, and 95% of people who have studied with us pass on their first try. Congrats in advance!"
      ],
      processTips: [
        "Tip: Remember to <a href=\"https://www.cslb.ca.gov/Contractors/Applicants/Contractors_License/Exam_Application/Applying_For_License.aspx\">submit your application</a> with CSLB so you can book your test ASAP.",
        "",
        ""
      ],
      income: "Licensed contractors on average earn 56% more income annually and can command larger jobs, better margins, and more stable work. Don't just do the work, <em>run the job.</em>",
      valueEyebrow: "Why choose Study Trades?",
      valueTitles: [
        "It's Fast.",
        "It's Flexible.",
        "It's Proven.",
        "Detailed Study Materials",
        "Practice Quizzes & Exams",
        "A task list for getting licensed fast"
      ],
      valueItems: [
        "Get prepped for your license as fast as 1-2 months or at your own pace.",
        "Our service is built for working people, you can study and test anywhere at anytime.",
        "Our success rate speaks for itself. Nearly every person who has used Studytrades has passed their exams.",
        "Read or listen to focused lessons built around your license classification.",
        "Test your knowledge with quizzes and practice exams that mirror the real thing.",
        "Stay organized from application to test day with a clear licensing checklist."
      ],
      darkHeading: "Becoming a Licensed Contractor in California",
      checklistHeading: "Do you meet the requirements?",
      checklistItems: [
        "I'm a legal citizen at least 18 years of age",
        "I have 4 years of journey-level experience",
        "I do work that is $1000+ (Labor + Materials)"
      ],
      costHeading: "What it costs in 2026",
      costIntro: "The reality is, there are costs associated with getting your license, the good news is we're here to guide you. Study Trades will provide you with resources and guidance, along with best in class test prep. Here's what to expect:",
      costApplication: "<strong>State Application Fee:</strong> $450 <span>(license renewal every 2 years)</span>",
      costQuestions: [
        "A $25,000 contractor bond?! How does that work?",
        "How does workers' comp insurance work for me?"
      ],
      timelineHeading: "Here's how you can get licensed fast with Study Trades",
      calendarHeader: ["Monthly timeline", "California license path"],
      timelineTitles: [
        "Submit application",
        "Study and exam prep",
        "Application approved, schedule test",
        "Take and pass test",
        "Finish issuance",
        "You're licensed"
      ],
      timelineBodies: [
        "Processing often takes 4 to 6 weeks before you are cleared for testing.",
        "StudyTrades is built as an online-only fast-track, so you can prep while the CSLB is processing instead of waiting around.",
        "Once you are ready, check availability and schedule your test via CSLB.",
        "Your test will be taken at a test center, but don't worry, we've set you up for success.",
        "Finish the last state items: contractor bond, insurance, fingerprinting, and the final license fee.",
        "<em>Average</em> time to complete takes 6 to 9 months from start to issued license."
      ],
      contentEyebrow: "Exam Prep",
      contentHeading: "See how the study app actually works",
      contentBody: "Short sections, clear audio-friendly lessons, and just enough structure to help you move through the material without getting lost.",
      contentHeader: ["California B General Building", "Inside the fast-track study app"],
      contentTopicTitle: "A clear chapter map, not a pile of random lessons",
      contentTopicSummary: "The app breaks the material into a clean chapter list so you can move through the exam outline in order, track progress, and jump straight into the matching quiz.",
      contentPoints: [
        "Eligibility and CSLB basics",
        "Contracts and change orders",
        "Business law and jobsite rules",
        "Project management and scheduling",
        "Final exam review and test readiness"
      ],
      quizEyebrow: "Practice test",
      quizHeading: "Then test yourself right away",
      quizBody: "The quiz layer is tied directly to the lesson, so you can read the rule, answer five focused questions, and see if the concept actually stuck.",
      quizHeader: ["Sample question set", "Question 1 of 5"],
      quizQuestion: "Which change most likely requires a written change order before the work keeps moving?",
      quizAnswers: [
        "The client asks for progress photos",
        "The scope changes and the price changes with it",
        "A supplier confirms a delivery window",
        "A worker swaps a shift with another worker"
      ],
      planTitle: "Single license fast track",
      planBody: "One license. Online prep. App access. Practice questions. A faster path from application to exam day.",
      planButton: "Join the waitlist",
      reasonsHeading: "WHY PEOPLE CHOOSE STUDYTRADES.COM",
      testimonialQuotes: [
        "\"I liked that the plumbing material was organized around what I actually needed to study. It made the next step feel much clearer.\"",
        "\"The B General prep helped me stay focused instead of jumping between random notes and videos. I could see what to work on next.\"",
        "\"I wanted a cleaner path from application to exam prep. Study Trades made it easier to understand the process and keep moving.\""
      ],
      testimonialNames: ["Jose", "Christina", "Nick"],
      testimonialLicenses: [
        "C-36 Plumbing",
        "B General Building",
        "B General Building"
      ],
      blogHeading: "Let's do this together.",
      blogCategories: ["I havent started", "I need clarity", "I have questions"],
      blogTitles: [
        "I want a no-nonsense way to pick my license and get launch updates.",
        "I want to understand eligibility before I spend money on prep.",
        "I want quick answers before I choose a license path."
      ],
      blogLinks: ["Join the waitlist", "Start with the basics", "Read the FAQ"],
      faqEyebrow: "",
      faqHeading: "Frequently Asked:",
      faqBody: "",
      faqQuestions: [
        "Do I need a contractor license for the work I want to do?",
        "How do I know which exam I need?",
        "What if I am not sure I qualify yet?",
        "Which study option is right for me?",
        "Do I need books, tabs, or practice exams?",
        "What if I already failed once?",
        "Will someone help me choose the right path?",
        "What happens after I pass?"
      ],
      faqAnswers: [
        "That depends on your state, your trade, and the kind of work you want to contract for. In California, the safest way to think about it is this: if you want to operate as the contractor and take on larger jobs, you should confirm the classification and licensing rules before you guess your way into a problem.",
        "In California, most applicants take the Law and Business exam plus the trade exam for their classification. Some classifications or situations are handled differently, and CSLB can waive exams in limited cases. The point is to match the exam to the actual license path, not just the kind of work you do day to day.",
        "That is common. In California, CSLB generally wants at least four years of journey-level, foreman, supervising employee, contractor, or owner-builder experience in the classification you are applying for. The real issue is usually not ambition. It is whether your experience can be documented the right way.",
        "If you already know your exam and you are good at staying on track, self-paced may be enough. If you work full-time, feel rusty, failed before, or just do better with structure, you probably want a more guided option. Buying more than you need is dumb. Buying too little and having to retake the exam is also dumb.",
        "For California contractor exams, the standard trade and Law and Business exams are computer-based and generally closed book, so you are using books and reference material to study, not carrying them into the testing room. What you do need is material that matches the actual exam outline, plus enough practice to stop second-guessing yourself.",
        "That usually means you need a better plan, not just more random material. Most repeat test-takers do better when they get clear on what exam they are actually taking, where they broke down the first time, and how to study with more structure instead of just doing more of the same.",
        "That is the idea. A lot of people are not ready to buy study prep until they understand what license path they are aiming at, whether Law and Business applies, and what the next paperwork step is. We are trying to make that part clearer before you spend money in the wrong direction.",
        "Passing is a big step, but it is not the finish line. In California, you may still need to pay issuance fees and submit things like your contractor bond, workers’ compensation insurance or exemption, and other entity paperwork before the license is issued and active. That is why passing the exam and finishing the licensing process are related, but not the same problem."
      ],
      faqMore: "I have more questions"
    },
    es: {
      i18n: {
        brand_name: "Study Trades",
        nav_how: "Cómo funciona",
        nav_plans: "Planes",
        nav_faq: "Preguntas",
        nav_contact: "Contacto",
        nav_cta: "Empieza tu preparación",
        nav_cta_mobile: "Empezar",
        footer_brand: "Study Trades",
        footer_intro: "Guía práctica de licencias para contratistas, preparación para el examen y herramientas de estudio para adultos que trabajan.",
        footer_col1: "Comenzar",
        footer_how: "Cómo funciona",
        footer_plans: "Planes",
        footer_waitlist: "Lista de espera",
        footer_col2: "Ayuda",
        footer_faq: "Preguntas",
        footer_contact: "Contacto",
        footer_about: "Acerca de",
        footer_student_support: "Soporte",
        footer_col3: "Políticas",
        footer_privacy: "Privacidad",
        footer_terms: "Términos",
        footer_refund: "Reembolsos",
        footer_accessibility: "Accesibilidad",
        footer_col4: "Contacto",
        footer_hours: "Lun-Vie, 8am-5pm PT",
        footer_login: "Ingreso del alumno"
      },
      heroLines: ["Obtén tu licencia,", "sin adivinar."],
      heroSubheading: "Study Trades ayuda a futuros contratistas y contratistas actuales a entender su camino de licencia, prepararse para sus exámenes y mantenerse organizados desde la solicitud hasta el día del examen.",
      selectorDefault: "Selecciona tu licencia",
      selectorButton: "Empieza tu preparación",
      processLabels: ["1", "2", "3"],
      processTitles: ["Aprende desde cualquier lugar", "Estudia tu oficio", "Aprueba tu examen"],
      processBodies: [
        "Aprende desde cualquier lugar y en cualquier momento con nuestro proceso guiado e interactivo de estudio y preparación para el examen.",
        "Lee o escucha lecciones específicas para tu clasificación de oficio, haz cuestionarios y mide tus conocimientos con exámenes de práctica que se parecen al examen real.",
        "Cuando tus puntajes de práctica sean buenos, estarás listo para aprobar. Agenda tu examen en un centro oficial de pruebas y agradécenos cuando apruebes y te conviertas en contratista con licencia."
      ],
      processTips: [
        "Consejo: Recuerda <a href=\"https://www.cslb.ca.gov/Contractors/Applicants/Contractors_License/Exam_Application/Applying_For_License.aspx\">enviar tu solicitud</a> al CSLB para poder agendar tu examen en cuanto estés listo.",
        "",
        ""
      ],
      income: "Los contratistas con licencia ganan en promedio 56% más al año y pueden conseguir trabajos más grandes, mejores márgenes y una carga de trabajo más estable. No solo hagas el trabajo, <em>dirige el trabajo.</em>",
      valueEyebrow: "¿Por qué elegir Study Trades?",
      valueTitles: [
        "Es rápido.",
        "Es flexible.",
        "Está comprobado.",
        "Materiales de estudio detallados",
        "Cuestionarios y exámenes de práctica",
        "Una lista de tareas para licenciarte rápido"
      ],
      valueItems: [
        "Prepárate para tu licencia en tan solo 1 a 2 meses o a tu propio ritmo.",
        "Nuestro servicio está hecho para gente que trabaja; puedes estudiar y presentar desde cualquier lugar y en cualquier momento.",
        "Nuestra tasa de éxito habla por sí sola. Casi todas las personas que han usado Studytrades han aprobado sus exámenes.",
        "Lee o escucha lecciones enfocadas en tu clasificación de licencia.",
        "Mide tu conocimiento con cuestionarios y exámenes de práctica que se parecen al examen real.",
        "Mantente organizado desde la solicitud hasta el examen con una lista clara de pasos."
      ],
      darkHeading: "Cómo convertirte en contratista con licencia en California",
      checklistHeading: "¿Cumples con los requisitos?",
      checklistItems: [
        "Soy ciudadano legal y tengo al menos 18 años",
        "Tengo 4 años de experiencia a nivel oficial",
        "Hago trabajos de $1000 o más (mano de obra + materiales)"
      ],
      costHeading: "Lo que cuesta en 2026",
      costIntro: "La realidad es que obtener tu licencia tiene costos, pero la buena noticia es que estamos aquí para orientarte. Study Trades te dará recursos, guía y preparación de primer nivel para el examen. Esto es lo que puedes esperar:",
      costApplication: "<strong>Cuota estatal de solicitud:</strong> $450 <span>(renovación de licencia cada 2 años)</span>",
      costQuestions: [
        "¿Un bono de contratista de $25,000? ¿Cómo funciona eso?",
        "¿Cómo funciona el seguro de compensación laboral para mí?"
      ],
      timelineHeading: "Así puedes obtener tu licencia rápido con Study Trades",
      calendarHeader: ["Cronograma mensual", "Ruta de licencia en California"],
      timelineTitles: [
        "Envía la solicitud",
        "Estudia y prepárate",
        "Solicitud aprobada, programa el examen",
        "Presenta y aprueba el examen",
        "Finaliza la emisión",
        "Ya tienes licencia"
      ],
      timelineBodies: [
        "El proceso suele tardar de 4 a 6 semanas antes de que te autoricen para presentar el examen.",
        "StudyTrades está diseñado como una vía rápida 100% en línea, para que te prepares mientras el CSLB procesa tu solicitud en lugar de esperar.",
        "Cuando estés listo, revisa disponibilidad y programa tu examen con el CSLB.",
        "Tu examen será en un centro de pruebas, pero no te preocupes, te vamos a preparar para tener éxito.",
        "Completa los últimos requisitos del estado: bono de contratista, seguro, huellas y la cuota final de licencia.",
        "El tiempo <em>promedio</em> para completar el proceso es de 6 a 9 meses desde el inicio hasta la licencia emitida."
      ],
      contentEyebrow: "Preparación",
      contentHeading: "Mira cómo funciona realmente la app de estudio",
      contentBody: "Secciones cortas, lecciones claras y aptas para audio, y la estructura justa para ayudarte a avanzar sin perderte.",
      contentHeader: ["California B General Building", "Dentro de la app acelerada"],
      contentTopicTitle: "Un mapa claro de capítulos, no un montón de lecciones sueltas",
      contentTopicSummary: "La app divide el material en una lista limpia de capítulos para que avances en orden, sigas tu progreso y saltes directo al cuestionario correspondiente.",
      contentPoints: [
        "Elegibilidad y bases del CSLB",
        "Contratos y órdenes de cambio",
        "Leyes del negocio y reglas de obra",
        "Gestión de proyectos y programación",
        "Repaso final y preparación del examen"
      ],
      quizEyebrow: "Práctica",
      quizHeading: "Luego ponte a prueba de inmediato",
      quizBody: "La parte de preguntas está conectada directamente con la lección, para que leas la regla, respondas cinco preguntas concretas y veas si el concepto realmente se quedó contigo.",
      quizHeader: ["Set de preguntas de muestra", "Pregunta 1 de 5"],
      quizQuestion: "¿Qué cambio requiere con mayor probabilidad una orden de cambio por escrito antes de continuar con el trabajo?",
      quizAnswers: [
        "El cliente pide fotos del avance",
        "Cambia el alcance del trabajo y también el precio",
        "Un proveedor confirma una fecha de entrega",
        "Un trabajador cambia turno con otro trabajador"
      ],
      planTitle: "Ruta rápida para una sola licencia",
      planBody: "Una licencia. Preparación en línea. Acceso a la app. Preguntas de práctica. Una ruta más rápida desde la solicitud hasta el día del examen.",
      planButton: "Únete a la lista",
      reasonsHeading: "POR QUÉ ELIGEN STUDYTRADES.COM",
      testimonialQuotes: [
        "\"Me gustó que el material de plomería estuviera organizado alrededor de lo que realmente necesitaba estudiar. Hizo que el siguiente paso fuera mucho más claro.\"",
        "\"La preparación de B General me ayudó a mantenerme enfocada en vez de saltar entre notas y videos al azar. Podía ver qué trabajar después.\"",
        "\"Quería una ruta más clara desde la solicitud hasta la preparación del examen. Study Trades hizo más fácil entender el proceso y seguir avanzando.\""
      ],
      testimonialNames: ["Jose", "Christina", "Nick"],
      testimonialLicenses: [
        "C-36 Plomería",
        "B General Building",
        "B General Building"
      ],
      blogHeading: "Hagámoslo juntos.",
      blogCategories: ["No he empezado", "Necesito claridad", "Tengo preguntas"],
      blogTitles: [
        "Quiero una forma directa de elegir mi licencia y recibir actualizaciones del lanzamiento.",
        "Quiero entender la elegibilidad antes de gastar dinero en preparación.",
        "Quiero respuestas rápidas antes de elegir una ruta de licencia."
      ],
      blogLinks: ["Únete a la lista", "Empieza con lo básico", "Lee las preguntas"],
      faqEyebrow: "",
      faqHeading: "Preguntas frecuentes:",
      faqBody: "",
      faqQuestions: [
        "¿Necesito una licencia de contratista para el trabajo que quiero hacer?",
        "¿Cómo sé qué examen necesito?",
        "¿Qué pasa si no estoy seguro de que ya califico?",
        "¿Qué opción de estudio es la adecuada para mí?",
        "¿Necesito libros, pestañas o exámenes de práctica?",
        "¿Qué pasa si ya reprobé una vez?",
        "¿Alguien me ayudará a elegir la ruta correcta?",
        "¿Qué pasa después de aprobar?"
      ],
      faqAnswers: [
        "Eso depende de tu estado, tu oficio y del tipo de trabajo que quieres contratar. En California, la forma más segura de verlo es esta: si quieres operar como el contratista y tomar trabajos más grandes, debes confirmar la clasificación y las reglas de licencia antes de meterte en un problema por adivinar.",
        "En California, la mayoría de los solicitantes presentan el examen de Law and Business junto con el examen del oficio para su clasificación. Algunas clasificaciones o situaciones se manejan distinto, y CSLB puede eximir exámenes en casos limitados. El punto es conectar el examen con la ruta de licencia real, no solo con el tipo de trabajo que haces día a día.",
        "Eso es común. En California, CSLB generalmente quiere al menos cuatro años de experiencia como journey-level, capataz, empleado supervisor, contratista u owner-builder en la clasificación a la que aplicas. El problema real normalmente no es la ambición. Es si tu experiencia puede documentarse correctamente.",
        "Si ya sabes qué examen te toca y eres bueno para mantenerte enfocado, el autoestudio puede ser suficiente. Si trabajas tiempo completo, te sientes oxidado, ya reprobaste antes o simplemente rindes mejor con estructura, probablemente necesitas una opción más guiada. Comprar más de lo que necesitas es mala idea. Comprar muy poco y tener que repetir el examen también lo es.",
        "Para los exámenes de contratista en California, los exámenes estándar de oficio y Law and Business son computarizados y generalmente cerrados, así que usas libros y material de referencia para estudiar, no para llevarlos al examen. Lo que sí necesitas es material que coincida con el temario real del examen y suficiente práctica para dejar de dudar de ti mismo.",
        "Eso normalmente significa que necesitas un mejor plan, no más material al azar. A la mayoría de las personas que repiten les va mejor cuando aclaran qué examen realmente van a tomar, en qué fallaron la primera vez y cómo estudiar con más estructura en lugar de hacer más de lo mismo.",
        "Esa es la idea. Mucha gente no está lista para comprar preparación hasta entender qué ruta de licencia está persiguiendo, si aplica Law and Business y cuál es el siguiente paso de papeleo. Estamos tratando de hacer esa parte más clara antes de que gastes dinero en la dirección equivocada.",
        "Aprobar es un gran paso, pero no es la meta final. En California, todavía puede que tengas que pagar tarifas de emisión y entregar cosas como tu contractor bond, workers’ compensation o exención, y otros documentos de entidad antes de que la licencia quede emitida y activa. Por eso aprobar el examen y terminar el proceso de licencia están relacionados, pero no son el mismo problema."
      ],
      faqMore: "Tengo más preguntas"
    }
  };

  const setText = (selector, value) => {
    const node = document.querySelector(selector);
    if (node && value !== undefined) node.textContent = value;
  };
  const setHTML = (selector, value) => {
    const node = document.querySelector(selector);
    if (node && value !== undefined) node.innerHTML = value;
  };
  const setTextList = (selector, values) => {
    document.querySelectorAll(selector).forEach((node, index) => {
      if (values[index] !== undefined) node.textContent = values[index];
    });
  };
  const setHTMLList = (selector, values) => {
    document.querySelectorAll(selector).forEach((node, index) => {
      if (values[index] !== undefined) node.innerHTML = values[index];
    });
  };

  function applyLanguage(language) {
    const copy = translations[language] || translations.en;
    document.documentElement.lang = language;
    document.body.setAttribute("data-language", language);

    document.querySelectorAll("[data-i18n]").forEach((node) => {
      const key = node.getAttribute("data-i18n");
      if (copy.i18n[key]) node.textContent = copy.i18n[key];
    });

    setTextList(".hero-section h1 span", copy.heroLines);
    setText(".hero-section .rte p", copy.heroSubheading);
    setText("[data-license-selector] [data-license-submit]", copy.selectorButton);

    const licensePlaceholder = document.querySelector("[data-license-selector] [data-license-select] option[value='']");
    if (licensePlaceholder) licensePlaceholder.textContent = copy.selectorDefault;

    setTextList(".process-step__label", copy.processLabels);
    setTextList(".process-step h3", copy.processTitles);
    setTextList(".process-step > p", copy.processBodies);
    setHTMLList(".process-step__tip", copy.processTips);

    setHTML(".license-value-statement__inner h2", copy.income);
    setText(".mini-value-grid .section-eyebrow", copy.valueEyebrow);
    setTextList(".mini-value-card h3", copy.valueTitles);
    setTextList(".mini-value-card p", copy.valueItems);

    setText(".requirements-story .section-heading h2", copy.darkHeading);
    setText(".requirements-story__content--checklist-centered h3", copy.checklistHeading);
    setTextList(".requirements-story__checklist li span:last-child", copy.checklistItems);
    setText(".requirements-story__cost-heading", copy.costHeading);
    setText(".requirements-story__intro", copy.costIntro);
    setHTML(".requirements-story__points li", copy.costApplication);
    setTextList(".requirements-story__cost-question-link > span:first-child", copy.costQuestions);
    setText(".requirements-story__content--timeline-intro h2", copy.timelineHeading);
    setTextList(".requirements-story__calendar-header > *", copy.calendarHeader);
    setTextList(".requirements-story__timeline-copy strong", copy.timelineTitles);
    setHTMLList(".requirements-story__timeline-copy p", copy.timelineBodies);

    setText(".preview-section--content .section-eyebrow", copy.contentEyebrow);
    setText(".preview-section--content .preview-section__intro h2", copy.contentHeading);
    setText(".preview-section--content .preview-section__intro p:last-child", copy.contentBody);
    setText("[data-content-header='section']", copy.contentHeader[0]);
    setText("[data-content-header='chapter']", copy.contentHeader[1]);
    setText(".content-preview-card__body h3", copy.contentTopicTitle);
    setText(".content-preview-card__body p", copy.contentTopicSummary);
    setTextList(".content-preview-card__chapter-list li", copy.contentPoints);

    setText(".preview-section--question .section-eyebrow", copy.quizEyebrow);
    setText(".preview-section--question .preview-section__intro h2", copy.quizHeading);
    setText(".preview-section--question .preview-section__intro p:last-child", copy.quizBody);
    setTextList(".question-preview-card__header > *", copy.quizHeader);
    setText(".question-preview-card__prompt", copy.quizQuestion);
    setTextList(".question-preview-card__answer", copy.quizAnswers);

    setText(".plan-card__copy h3", copy.planTitle);
    setText(".plan-card__copy p", copy.planBody);
    setText(".plan-card__price .button", copy.planButton);

    setText(".reasons-testimonials .section-eyebrow", copy.reasonsHeading);
    setTextList(".testimonial-card__quote", copy.testimonialQuotes);
    setTextList(".testimonial-card__name", copy.testimonialNames);
    setTextList(".testimonial-card__license", copy.testimonialLicenses);
    setText(".blog-post-grid-section .section-heading h2", copy.blogHeading);
    setTextList(".blog-post-card__meta", copy.blogCategories);
    setTextList(".blog-post-card h3", copy.blogTitles);
    setTextList(".blog-post-card__link span:first-child", copy.blogLinks);

    const faqEyebrowNode = document.querySelector(".faq-section .section-eyebrow");
    if (faqEyebrowNode) {
      faqEyebrowNode.textContent = copy.faqEyebrow;
      faqEyebrowNode.style.display = copy.faqEyebrow ? "" : "none";
    }
    setText(".faq-section .section-heading h2", copy.faqHeading);
    const faqBodyNode = document.querySelector(".faq-section .section-heading p");
    if (faqBodyNode) {
      faqBodyNode.textContent = copy.faqBody;
      faqBodyNode.style.display = copy.faqBody ? "" : "none";
    }
    setTextList(".faq-item summary", copy.faqQuestions);
    setTextList(".faq-item .rte p", copy.faqAnswers);
    setText(".faq-section__more a", copy.faqMore);

    try {
      window.localStorage.setItem("studyTradesLanguage", language);
    } catch (_error) {
      // Ignore localStorage failures.
    }

    languageSelects.forEach((select) => {
      select.value = language;
      const switchRoot = select.closest("[data-language-switch]");
      const flagNode = switchRoot?.querySelector("[data-language-flag]");
      if (flagNode) flagNode.textContent = languageFlags[language] || languageFlags.en;
    });
  }

  let initialLanguage = "en";
  try {
    initialLanguage = window.localStorage.getItem("studyTradesLanguage") || "en";
  } catch (_error) {
    initialLanguage = "en";
  }
  applyLanguage(initialLanguage);

  languageSelects.forEach((select) => {
    select.addEventListener("change", () => {
      applyLanguage(select.value || "en");
    });
  });
}

function initializeTestimonialTouchPause() {
  const rails = document.querySelectorAll(".testimonial-rail");
  if (!rails.length) return;

  const pause = (rail) => {
    rail.style.animationPlayState = "paused";
  };

  const resume = (rail) => {
    rail.style.animationPlayState = "";
  };

  rails.forEach((rail) => {
    rail.addEventListener("touchstart", () => pause(rail), { passive: true });
    rail.addEventListener("touchend", () => resume(rail), { passive: true });
    rail.addEventListener("touchcancel", () => resume(rail), { passive: true });
  });
}

function initializeAmbientSectionMotion() {
  const targets = document.querySelectorAll([
    ".preview-section__intro",
    ".content-preview-card",
    ".question-preview-card",
    ".plan-card",
    ".reasons-testimonials .section-heading",
    ".testimonial-marquee"
  ].join(","));

  if (!targets.length) return;

  if (!("IntersectionObserver" in window)) {
    targets.forEach((node) => node.classList.add("is-visible"));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add("is-visible");
      observer.unobserve(entry.target);
    });
  }, {
    threshold: 0.14,
    rootMargin: "0px 0px -8% 0px"
  });

  targets.forEach((node) => observer.observe(node));
}

function initializeAmbientBand() {
  if (!document.body.classList.contains("template-index")) return;

  const startNode = document.querySelector(".preview-section--content");
  const endNode = document.querySelector(".reasons-testimonials");
  const shell = document.querySelector(".site-shell");
  if (!startNode || !endNode || !shell) return;

  const syncBand = () => {
    const top = startNode.offsetTop;
    const height = (endNode.offsetTop + endNode.offsetHeight) - startNode.offsetTop;

    document.documentElement.style.setProperty("--ambient-band-top", `${Math.max(0, top)}px`);
    document.documentElement.style.setProperty("--ambient-band-height", `${Math.max(0, height)}px`);
    document.documentElement.style.setProperty("--ambient-band-opacity", "1");
  };

  syncBand();
  window.addEventListener("resize", syncBand, { passive: true });
  window.addEventListener("load", syncBand, { passive: true });
}

function initializeSectionParallax() {
  if (!document.body.classList.contains("template-index")) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const layers = [
    { node: document.querySelector(".mini-value-grid .page-width"), speed: -0.065 },
    { node: document.querySelector(".requirements-story .page-width"), speed: -0.05 }
  ].filter((item) => item.node);

  if (!layers.length) return;

  let ticking = false;

  const update = () => {
    const viewportHeight = window.innerHeight || 1;

    layers.forEach(({ node, speed }) => {
      const rect = node.getBoundingClientRect();
      const progress = ((rect.top + rect.height * 0.5) - viewportHeight * 0.5) * speed;
      const offset = Math.max(-28, Math.min(28, progress));
      node.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    });

    ticking = false;
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  update();
  window.addEventListener("scroll", requestUpdate, { passive: true });
  window.addEventListener("resize", requestUpdate, { passive: true });
}

function initializeLicenseSelectors() {
  const selectors = document.querySelectorAll("[data-license-selector]");

  selectors.forEach((root) => {
    const offersNode = root.querySelector("[data-license-offers]");
    const stateSelect = root.querySelector("[data-state-select]");
    const licenseSelect = root.querySelector("[data-license-select]");
    const submitButton = root.querySelector("[data-license-submit]");
    const nextStepUrl = root.getAttribute("data-next-step-url") || "/pages/signup";
    const waitlistEndpoint = root.getAttribute("data-waitlist-endpoint") || "";
    const stateModal = root.querySelector("[data-state-waitlist-modal]");
    const stateModalName = root.querySelector("[data-state-waitlist-name]");
    const stateModalEntry = root.querySelector("[data-state-waitlist-entry]");
    const stateModalSuccess = root.querySelector("[data-state-waitlist-success]");
    const stateModalSuccessState = root.querySelector("[data-state-waitlist-success-state]");
    const stateModalForm = root.querySelector("[data-state-waitlist-form]");
    const stateModalSelect = root.querySelector("[data-state-waitlist-select]");
    const stateModalLicense = root.querySelector("[data-state-waitlist-license]");
    const stateModalStatus = root.querySelector("[data-state-waitlist-status]");
    const stateModalCloseButtons = root.querySelectorAll("[data-state-waitlist-close]");

    if (!offersNode || !stateSelect || !licenseSelect || !submitButton) return;

    let offers = [];
    try {
      offers = JSON.parse(offersNode.textContent || "[]");
    } catch (_error) {
      offers = [];
    }

    function selectedStateOption() {
      return stateSelect.options[stateSelect.selectedIndex];
    }

    function syncSelectorLicenseOptions() {
      const choices = getLicenseChoicesForState(stateSelect.value, offers);
      const defaultSlug = stateSelect.value === STUDY_TRADES_PRIMARY_STATE ? STUDY_TRADES_PRIMARY_LICENSE_SLUG : "";
      populateLicenseOptions(licenseSelect, choices, defaultSlug);
      submitButton.disabled = !licenseSelect.value;
    }

    function selectedOffer() {
      return findOfferForSelection(offers, stateSelect.value, licenseSelect.value);
    }

    const selectedStateLabel = () => selectedStateOption()?.textContent || findStateLabel(stateSelect.value);
    const modalStateLabel = () => stateModalSelect?.options[stateModalSelect.selectedIndex]?.textContent || selectedStateLabel();

    if (stateModalSelect && !stateModalSelect.options.length) {
      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = "Select your state";
      placeholder.disabled = true;
      placeholder.selected = true;
      stateModalSelect.appendChild(placeholder);
      Array.from(stateSelect.options)
        .filter((option) => option.value !== STUDY_TRADES_PRIMARY_STATE)
        .forEach((option) => {
          stateModalSelect.appendChild(option.cloneNode(true));
        });
    }

    const closeStateWaitlistModal = () => {
      if (!stateModal) return;
      stateModal.hidden = true;
      document.body.classList.remove("state-waitlist-modal-open");
    };

    const showStateWaitlistEntry = () => {
      if (stateModalEntry) stateModalEntry.hidden = false;
      if (stateModalSuccess) stateModalSuccess.hidden = true;
    };

    const showStateWaitlistSuccess = (stateLabel) => {
      if (stateModalSuccessState) stateModalSuccessState.textContent = stateLabel;
      if (stateModalEntry) stateModalEntry.hidden = true;
      if (stateModalSuccess) stateModalSuccess.hidden = false;
    };

    const openStateWaitlistModal = (options = {}) => {
      if (!stateModal) return;
      if (!options.force && stateSelect.value === STUDY_TRADES_PRIMARY_STATE) return;
      showStateWaitlistEntry();
      if (stateModalSelect && stateSelect.value !== STUDY_TRADES_PRIMARY_STATE) {
        stateModalSelect.value = stateSelect.value;
      }
      if (stateModalName) stateModalName.textContent = modalStateLabel();
      if (stateModalStatus) stateModalStatus.textContent = "";
      stateModal.hidden = false;
      document.body.classList.add("state-waitlist-modal-open");
      trackStudyTradesEvent("waitlist_modal_open", {
        page_type: document.body.dataset.pageType,
        source_section: options.sourceSection || "site_waitlist"
      });
      stateModalSelect?.focus();
    };

    if (stateModalSelect) {
      stateModalSelect.addEventListener("change", () => {
        if (stateModalName) stateModalName.textContent = modalStateLabel();
      });
    }

    root.querySelectorAll("[data-state-waitlist-trigger]").forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        trackStudyTradesEvent("out_of_state_waitlist_click", {
          button_text: trigger.textContent.trim(),
          page_type: document.body.dataset.pageType,
          source_section: trigger.dataset.stSource || "site_navigation"
        });
        openStateWaitlistModal({ force: true, sourceSection: trigger.dataset.stSource });
      });
    });

    stateModalCloseButtons.forEach((button) => {
      button.addEventListener("click", closeStateWaitlistModal);
    });

    if (stateModalForm) {
      stateModalForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const formData = new FormData(stateModalForm);
        const email = String(formData.get("email") || "").trim();
        const requestedLicenseType = String(formData.get("licenseType") || "").trim();
        if (!email) {
          if (stateModalStatus) stateModalStatus.textContent = "Enter your email so we can notify you.";
          return;
        }
        if (!requestedLicenseType) {
          if (stateModalStatus) stateModalStatus.textContent = "Enter the license or trade type you are interested in.";
          stateModalLicense?.focus();
          return;
        }
        if (!waitlistEndpoint) return;

        const requestedStateCode = stateModalSelect?.value || stateSelect.value;
        const requestedStateLabel = modalStateLabel();
        if (!requestedStateCode || requestedStateCode === STUDY_TRADES_PRIMARY_STATE) {
          if (stateModalStatus) stateModalStatus.textContent = "Select your state so we know where to notify you.";
          return;
        }
        const requestedLicenseSlug = requestedLicenseType
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        if (stateModalStatus) stateModalStatus.textContent = "Saving...";
        try {
          const response = await studyTradesLeadRequest(waitlistEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email,
              source: "shopify-state-waitlist",
              stateCode: requestedStateCode,
              licenseSlug: requestedLicenseSlug || null,
              requestedLabel: `${requestedStateLabel} · ${requestedLicenseType}`,
              marketingOptIn: true
            })
          });
          if (!response.ok) throw new Error("State waitlist failed");
          trackStudyTradesEvent("waitlist_submit", {
            state: requestedStateCode,
            state_name: requestedStateLabel,
            license_interest: requestedLicenseType,
            source_page: window.location.pathname,
            source_section: "state_waitlist_modal"
          });
          stateModalForm.reset();
          showStateWaitlistSuccess(requestedStateLabel);
        } catch (_error) {
          if (stateModalStatus) stateModalStatus.textContent = "We couldn’t save that right now. Please try again.";
        }
      });
    }

    stateSelect.addEventListener("change", () => {
      syncSelectorLicenseOptions();
      openStateWaitlistModal();
    });
    licenseSelect.addEventListener("change", () => {
      submitButton.disabled = !licenseSelect.value;
    });

    submitButton.addEventListener("click", () => {
      const match = selectedOffer();
      const licenseChoice = licenseSelect.options[licenseSelect.selectedIndex];
      if (!licenseSelect.value || !licenseChoice) return;

      if (stateSelect.value !== STUDY_TRADES_PRIMARY_STATE) {
        openStateWaitlistModal();
        return;
      }

      const stateLabel = selectedStateLabel();
      const licenseLabel = licenseChoice.textContent || "Selected license";

      const launchedSelection = Boolean(match?.available);
      const params = new URLSearchParams({
        stateCode: stateSelect.value,
        stateLabel,
        licenseSlug: licenseSelect.value,
        licenseLabel,
        available: launchedSelection ? "1" : "0",
        status: match?.status || "waitlist",
        allowCheckout: offerCanCheckout(match) ? "1" : "0",
        priceLabel: match?.priceLabel || "",
        productUrl: match?.productUrl || ""
      });

      window.location.href = `${nextStepUrl}?${params.toString()}`;
    });

    const boot = async (force = false) => {
      const syncedOffers = await loadStudyTradesOffers({ force });
      offers = mergeOffersWithOfferAvailability(offers, syncedOffers);

      if (Array.from(stateSelect.options).some((option) => option.value === "CA")) {
        stateSelect.value = "CA";
      }

      syncSelectorLicenseOptions();
    };

    const refreshFromVisibility = () => {
      if (document.visibilityState === "visible") {
        void boot(true);
      }
    };

    window.addEventListener("focus", () => {
      void boot(true);
    });
    document.addEventListener("visibilitychange", refreshFromVisibility);

    boot(true);
  });
}

function initializeGlobalWaitlistCapture() {
  const root = document.querySelector("[data-global-waitlist-modal]");
  if (!root) return;

  const form = root.querySelector("[data-global-waitlist-form]");
  const stateSelect = root.querySelector("[data-global-waitlist-state]");
  const licenseInput = root.querySelector("[data-global-waitlist-license]");
  const emailInput = root.querySelector("[data-global-waitlist-email]");
  const honeypotInput = root.querySelector("[data-global-waitlist-honeypot]");
  const entry = root.querySelector("[data-global-waitlist-entry]");
  const success = root.querySelector("[data-global-waitlist-success]");
  const successState = root.querySelector("[data-global-waitlist-success-state]");
  const status = root.querySelector("[data-global-waitlist-status]");
  const closeButtons = root.querySelectorAll("[data-global-waitlist-close]");
  const endpoint = root.getAttribute("data-lead-endpoint") || "https://app.studytrades.com/api/license-interest";

  if (!form || !stateSelect || !licenseInput || !emailInput) return;

  stateSelect.innerHTML = '<option value="" disabled selected>Select your state</option>';
  STUDY_TRADES_STATE_OPTIONS
    .filter((state) => state.code !== STUDY_TRADES_PRIMARY_STATE)
    .forEach((state) => {
      const option = document.createElement("option");
      option.value = state.code;
      option.textContent = state.label;
      stateSelect.appendChild(option);
    });

  const closeModal = () => {
    root.hidden = true;
    document.body.classList.remove("state-waitlist-modal-open");
  };

  const openModal = (sourceSection = "site_waitlist") => {
    if (entry) entry.hidden = false;
    if (success) success.hidden = true;
    if (status) status.textContent = "";
    root.hidden = false;
    document.body.classList.add("state-waitlist-modal-open");
    trackStudyTradesEvent("waitlist_modal_open", {
      page_type: document.body.dataset.pageType,
      source_section: sourceSection
    });
    stateSelect.focus();
  };

  document.querySelectorAll("[data-state-waitlist-trigger]").forEach((trigger) => {
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      const sourceSection = trigger.dataset.stSource || "site_navigation";
      trackStudyTradesEvent("out_of_state_waitlist_click", {
        button_text: trigger.textContent.trim(),
        page_type: document.body.dataset.pageType,
        source_section: sourceSection
      });
      openModal(sourceSection);
    });
  });

  closeButtons.forEach((button) => button.addEventListener("click", closeModal));

  const params = new URLSearchParams(window.location.search);
  if (params.get("waitlist") === "1") {
    window.setTimeout(() => openModal(params.get("source") || "direct_waitlist_link"), 0);
  }

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const stateCode = stateSelect.value;
    const stateLabel = findStateLabel(stateCode);
    const licenseType = licenseInput.value.trim();
    const email = emailInput.value.trim();

    if (!stateCode || !licenseType || !email) {
      if (status) status.textContent = "Add your state, license or trade type, and email.";
      return;
    }

    const licenseSlug = licenseType
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    if (status) status.textContent = "Saving...";
    try {
      const response = await studyTradesLeadRequest(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          source: "shopify-state-waitlist",
          stateCode,
          licenseSlug: licenseSlug || null,
          requestedLabel: `${stateLabel} · ${licenseType}`,
          marketingOptIn: true,
          companyWebsite: honeypotInput?.value || ""
        })
      });
      if (!response.ok) throw new Error("Waitlist capture failed");

      trackStudyTradesEvent("waitlist_submit", {
        state: stateCode,
        state_name: stateLabel,
        license_interest: licenseType,
        source_page: window.location.pathname,
        source_section: params.get("source") || "global_waitlist_modal"
      });
      if (successState) successState.textContent = stateLabel;
      if (entry) entry.hidden = true;
      if (success) success.hidden = false;
      form.reset();
    } catch (_error) {
      if (status) status.textContent = "We couldn’t save that right now. Please try again.";
    }
  });
}

function initializeRequirementTooltips() {
  const triggers = document.querySelectorAll("[data-tooltip-trigger]");

  triggers.forEach((trigger) => {
    const tooltipId = trigger.getAttribute("data-tooltip-trigger");
    const tooltip = tooltipId ? document.getElementById(tooltipId) : null;
    if (!tooltip) return;

    const closeTooltip = () => {
      tooltip.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
    };

    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      const nextHidden = !tooltip.hidden;
      document.querySelectorAll(".requirements-story__tooltip").forEach((node) => {
        node.hidden = true;
      });
      document.querySelectorAll("[data-tooltip-trigger]").forEach((node) => {
        node.setAttribute("aria-expanded", "false");
      });
      tooltip.hidden = nextHidden;
      trigger.setAttribute("aria-expanded", nextHidden ? "false" : "true");
    });

    document.addEventListener("click", (event) => {
      if (!trigger.parentElement?.contains(event.target)) {
        closeTooltip();
      }
    });
  });
}

function initializeCourseCardOverlays() {
  const cards = Array.from(document.querySelectorAll("[data-course-card]"));
  if (!cards.length) return;

  const closeCards = (exceptCard = null) => {
    cards.forEach((card) => {
      if (card !== exceptCard) card.classList.remove("is-active");
    });
  };

  cards.forEach((card) => {
    card.addEventListener("click", (event) => {
      if (event.target.closest("a")) return;
      const willOpen = !card.classList.contains("is-active");
      closeCards(card);
      card.classList.toggle("is-active", willOpen);
    });

    card.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (event.target.closest("a")) return;
      event.preventDefault();
      const willOpen = !card.classList.contains("is-active");
      closeCards(card);
      card.classList.toggle("is-active", willOpen);
    });
  });

  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-course-card]")) return;
    closeCards();
  });
}

function initializeSignupEntryLinks() {
  const planLinks = document.querySelectorAll("[data-plan-signup-link]");
  if (!planLinks.length) return;

  planLinks.forEach((link) => {
    link.addEventListener("click", (event) => {
      const href = link.getAttribute("href") || "";
      if (!href.includes("/pages/signup")) return;

      const selectorRoot = document.querySelector("[data-license-selector]");
      const stateSelect = selectorRoot?.querySelector("[data-state-select]");
      const licenseSelect = selectorRoot?.querySelector("[data-license-select]");
      if (!stateSelect) return;

      const stateCode = stateSelect.value || "CA";
      const stateLabel = stateSelect.options[stateSelect.selectedIndex]?.textContent || findStateLabel(stateCode);
      const params = new URLSearchParams({
        stateCode,
        stateLabel
      });

      if (licenseSelect?.value) {
        params.set("licenseSlug", licenseSelect.value);
        params.set("licenseLabel", licenseSelect.options[licenseSelect.selectedIndex]?.textContent || "Selected license");
      }

      event.preventDefault();
      window.location.href = `/pages/signup?${params.toString()}`;
    });
  });
}

function initializeSignupBasicsPages() {
  const pages = document.querySelectorAll("[data-signup-basics]");
  if (!pages.length) return;

  pages.forEach((root) => {
    const leadEndpoint = root.getAttribute("data-lead-endpoint");
    const googleStartUrl = root.getAttribute("data-google-start-url") || "https://app.studytrades.com/auth/signup-google";
    const emailInput = root.querySelector("[data-signup-email]");
    const nextButton = root.querySelector("[data-signup-basics-next]");
    const googleButton = root.querySelector("[data-signup-google]");
    const statusNode = root.querySelector("[data-signup-status]");
    if (!emailInput || !nextButton) return;

    const initial = buildSignupSelection();
    writeStoredSignupLead(initial);
    emailInput.value = initial.email || "";

    const setStatus = (message) => {
      if (statusNode) statusNode.textContent = message || "";
    };

    const currentSelection = () => ({
      ...buildSignupSelection(),
      email: emailInput.value.trim().toLowerCase()
    });

    nextButton.addEventListener("click", async () => {
      const selection = currentSelection();
      if (!selection.email) {
        setStatus("Add your email first so we can keep this moving.");
        return;
      }

      setStatus("Saving your details...");

      try {
        if (leadEndpoint) {
          const response = await studyTradesLeadRequest(leadEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              firstName: selection.firstName,
              lastName: selection.lastName,
              email: selection.email,
              stateCode: selection.stateCode,
              licenseSlug: selection.licenseSlug,
              requestedLabel: selection.requestedLabel,
              marketingOptIn: false,
              source: "shopify-signup-start"
            })
          });
          if (!response.ok) throw new Error("Lead capture failed");
        }

        writeStoredSignupLead(selection);
        window.location.href = `/pages/signup-profile?${buildSignupQuery(selection)}`;
      } catch (_error) {
        setStatus("We couldn’t save that just yet. Try again in a moment.");
      }
    });

    if (googleButton) {
      googleButton.setAttribute("href", googleStartUrl);
      googleButton.addEventListener("click", () => {
        writeStoredSignupLead(currentSelection());
      });
    }
  });
}

function initializeSignupProfilePages() {
  const pages = document.querySelectorAll("[data-signup-profile]");
  if (!pages.length) return;

  pages.forEach((root) => {
    const leadEndpoint = root.getAttribute("data-lead-endpoint");
    const stateSelect = root.querySelector("[data-signup-state-select]");
    const licenseSelect = root.querySelector("[data-signup-license-select]");
    const firstNameInput = root.querySelector("[data-signup-first-name]");
    const lastNameInput = root.querySelector("[data-signup-last-name]");
    const nextButton = root.querySelector("[data-signup-profile-next]");
    const statusNode = root.querySelector("[data-signup-status]");
    if (!stateSelect || !licenseSelect || !firstNameInput || !lastNameInput || !nextButton) return;

    const setStatus = (message) => {
      if (statusNode) statusNode.textContent = message || "";
    };

    let initial = buildSignupSelection();

    const currentSelection = () => {
      const stateCode = stateSelect.value || "CA";
      const stateLabel = stateSelect.options[stateSelect.selectedIndex]?.textContent || findStateLabel(stateCode);
      const licenseSlug = licenseSelect.value || "";
      const licenseLabel = licenseSelect.options[licenseSelect.selectedIndex]?.textContent || "";
      const offer = findOfferForSelection(studyTradesOffersCache, stateCode, licenseSlug);
      return {
        firstName: firstNameInput.value.trim(),
        lastName: lastNameInput.value.trim(),
        email: (buildSignupSelection().email || initial.email || "").trim().toLowerCase(),
        stateCode,
        stateLabel,
        licenseSlug,
        licenseLabel,
        requestedLabel: licenseLabel ? `${stateLabel} · ${licenseLabel}` : stateLabel,
        available: Boolean(offer?.available),
        productUrl: offer?.productUrl || "",
        priceLabel: offer?.priceLabel || ""
      };
    };

    stateSelect.addEventListener("change", () => {
      populateLicenseOptions(licenseSelect, getLicenseChoicesForState(stateSelect.value, studyTradesOffersCache));
      setStatus("");
    });

    nextButton.addEventListener("click", async () => {
      const selection = currentSelection();
      if (!selection.email) {
        setStatus("Start with email or Google first.");
        return;
      }
      if (!selection.firstName || !selection.lastName || !selection.licenseSlug) {
        setStatus("Fill out all four fields so we can keep this moving.");
        return;
      }

      setStatus("Saving your details...");

      try {
        if (leadEndpoint) {
          const response = await studyTradesLeadRequest(leadEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              firstName: selection.firstName,
              lastName: selection.lastName,
              email: selection.email,
              stateCode: selection.stateCode,
              licenseSlug: selection.licenseSlug,
              requestedLabel: selection.requestedLabel,
              marketingOptIn: false,
              source: selection.available ? "shopify-signup" : "shopify-site"
            })
          });
          if (!response.ok) throw new Error("Lead capture failed");
        }

        writeStoredSignupLead(selection);
        const nextPath = selection.available ? "/pages/eligibility" : "/pages/track-unavailable";
        window.location.href = `${nextPath}?${buildSignupQuery(selection)}`;
      } catch (_error) {
        setStatus("We couldn’t save that just yet. Try again in a moment.");
      }
    });

    const boot = async (force = false) => {
      await loadStudyTradesOffers({ force });
      initial = buildSignupSelection();
      populateStateOptions(stateSelect, initial.stateCode);
      populateLicenseOptions(licenseSelect, getLicenseChoicesForState(initial.stateCode, studyTradesOffersCache), initial.licenseSlug);
      firstNameInput.value = initial.firstName || "";
      lastNameInput.value = initial.lastName || "";
    };

    const refreshFromVisibility = () => {
      if (document.visibilityState === "visible") {
        void boot(true);
      }
    };

    window.addEventListener("focus", () => {
      void boot(true);
    });
    document.addEventListener("visibilitychange", refreshFromVisibility);

    boot(true);
  });
}

function initializeSignupEligibilityPages() {
  const pages = document.querySelectorAll("[data-signup-eligibility]");
  if (!pages.length) return;

  pages.forEach((root) => {
    const leadEndpoint = root.getAttribute("data-lead-endpoint");
    const checkoutUrl = root.getAttribute("data-checkout-url") || "/pages/checkout";
    const ineligibleUrl = root.getAttribute("data-ineligible-url") || "/pages/ineligible";
    const nextButton = root.querySelector("[data-signup-eligibility-next]");
    const statusNode = root.querySelector("[data-signup-status]");
    const statePill = root.querySelector("[data-eligibility-state-pill]");
    const licensePill = root.querySelector("[data-eligibility-license-pill]");
    if (!nextButton) return;

        const selection = buildSignupSelection();
    writeStoredSignupLead(selection);

    if (statePill) {
      statePill.textContent = selection.stateLabel || "California";
    }
    if (licensePill) {
      licensePill.textContent = selection.licenseLabel || "Selected license";
    }

    const setStatus = (message) => {
      if (statusNode) statusNode.textContent = message || "";
    };

    const normalizeReadiness = () => {
      const ageValue = root.querySelector("input[name='eligibility-age']:checked")?.value || "";
      const experienceValue = root.querySelector("input[name='eligibility-experience']:checked")?.value || "";
      const idValue = root.querySelector("input[name='eligibility-id']:checked")?.value || "";
      const applicationValue = root.querySelector("input[name='eligibility-application']:checked")?.value || "";
      if (!ageValue || !experienceValue || !idValue || !applicationValue) return null;

      let readinessStatus = "unsure";
      if (ageValue === "yes" && experienceValue === "qualified" && idValue === "yes") {
        readinessStatus = "ready";
      } else if (ageValue === "no" || experienceValue === "building" || idValue === "no") {
        readinessStatus = "needs-work";
      }

      return {
        ageConfirmed: ageValue === "yes",
        experienceStatus: experienceValue,
        ssnOrItinConfirmed: idValue === "yes" ? true : idValue === "no" ? false : null,
        readinessStatus,
        applicationStage: applicationValue === "yes" ? "submitted" : "none"
      };
    };

    nextButton.addEventListener("click", async () => {
      const readiness = normalizeReadiness();
      if (!readiness) {
        setStatus("Answer all four questions first so we can point you to the right next step.");
        return;
      }

      setStatus("");

      try {
        if (leadEndpoint) {
          const response = await studyTradesLeadRequest(leadEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              firstName: selection.firstName,
              lastName: selection.lastName,
              email: selection.email,
              stateCode: selection.stateCode,
              licenseSlug: selection.licenseSlug,
              requestedLabel: selection.requestedLabel,
              marketingOptIn: false,
              source: "shopify-signup",
              ...readiness
            })
          });
          if (!response.ok) throw new Error("Lead capture failed");
        }

        const merged = writeStoredSignupLead({ ...selection, ...readiness });
        const params = new URLSearchParams(buildSignupQuery(selection));
        params.set("readinessStatus", readiness.readinessStatus);
        params.set("applicationStage", readiness.applicationStage);

        if (readiness.readinessStatus === "ready") {
          window.location.href = `${selection.productUrl || checkoutUrl}?${params.toString()}`;
          return;
        }

        window.location.href = `${ineligibleUrl}?${params.toString()}`;
        void merged;
      } catch (_error) {
        setStatus("We couldn’t save that just yet. Try again in a moment.");
      }
    });
  });
}

function initializeSignupUnavailablePages() {
  const pages = document.querySelectorAll("[data-signup-unavailable]");
  if (!pages.length) return;

  pages.forEach((root) => {
    const leadEndpoint = root.getAttribute("data-lead-endpoint");
    const titleNode = root.querySelector("[data-unavailable-title]");
    const bodyNode = root.querySelector("[data-unavailable-body]");
    const optInButton = root.querySelector("[data-unavailable-optin]");
    const declineButton = root.querySelector("[data-unavailable-decline]");
    const statusNode = root.querySelector("[data-signup-status]");
    if (!optInButton || !declineButton) return;

    const selection = buildSignupSelection();
    writeStoredSignupLead(selection);

    if (titleNode) {
      titleNode.textContent = `We’re still working out the final details to launch in ${selection.stateLabel}.`;
    }
    if (bodyNode) {
      bodyNode.textContent = selection.licenseLabel
        ? `If you want, we’ll email you when ${selection.licenseLabel} is ready in ${selection.stateLabel} and send a launch discount when it goes live.`
        : `If you want, we’ll email you when contractor prep is ready in ${selection.stateLabel} and send a launch discount when it goes live.`;
    }

    const setStatus = (message) => {
      if (statusNode) statusNode.textContent = message || "";
    };

    async function submitWaitlist(marketingOptIn) {
      setStatus("Saving your request...");
      try {
        if (leadEndpoint) {
          const response = await studyTradesLeadRequest(leadEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              firstName: selection.firstName,
              lastName: selection.lastName,
              email: selection.email,
              stateCode: selection.stateCode,
              licenseSlug: selection.licenseSlug,
              requestedLabel: selection.requestedLabel,
              marketingOptIn,
              source: marketingOptIn ? "shopify-waitlist" : "shopify-site"
            })
          });
          if (!response.ok) throw new Error("Lead capture failed");
        }
        writeStoredSignupLead({ ...selection, marketingOptIn });
        setStatus(marketingOptIn ? "You’re on the list. We’ll reach out when it’s ready." : "Got it. We saved the demand without adding you to launch emails.");
      } catch (_error) {
        setStatus("We couldn’t save that just yet. Try again in a moment.");
      }
    }

    optInButton.addEventListener("click", () => submitWaitlist(true));
    declineButton.addEventListener("click", () => submitWaitlist(false));
  });
}

function initializeWaitlistSignupPages() {
  const pages = document.querySelectorAll("[data-waitlist-signup]");
  if (!pages.length) return;

  pages.forEach((root) => {
    const leadEndpoint = root.getAttribute("data-lead-endpoint");
    const form = root.querySelector("[data-waitlist-signup-form]");
    const stateSelect = root.querySelector("[data-waitlist-state]");
    const licenseSelect = root.querySelector("[data-waitlist-license]");
    const nameInput = root.querySelector("[data-waitlist-name]");
    const emailInput = root.querySelector("[data-waitlist-email]");
    const submitButton = root.querySelector("[data-waitlist-submit]");
    const statusNode = root.querySelector("[data-waitlist-status]");
    const successNode = root.querySelector("[data-waitlist-success]");
    const successCopyNode = root.querySelector("[data-waitlist-success-copy]");
    if (!form || !stateSelect || !licenseSelect || !nameInput || !emailInput) return;

    const params = new URLSearchParams(window.location.search);
    const initialSelection = buildSignupSelection(params);
    const stored = readStoredSignupLead();

    function setStatus(message) {
      if (statusNode) statusNode.textContent = message || "";
    }

    function splitName(fullName) {
      const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return { firstName: "", lastName: "" };
      return {
        firstName: parts[0],
        lastName: parts.slice(1).join(" ")
      };
    }

    function selectedLicenseLabel() {
      if (licenseSelect.value === "__not_sure__") return "I'm not sure yet";
      return licenseSelect.options[licenseSelect.selectedIndex]?.textContent || "";
    }

    function populateWaitlistLicenses(selectedSlug = "") {
      const choices = getLicenseChoicesForState(stateSelect.value, studyTradesOffersCache);
      licenseSelect.innerHTML = `
        <option value="">Select a license</option>
        <option value="__not_sure__">I'm not sure yet</option>
        ${choices.map((choice) => `
          <option value="${escapeHtml(choice.slug)}">${escapeHtml(choice.label)}</option>
        `).join("")}
      `;
      licenseSelect.value = choices.some((choice) => choice.slug === selectedSlug)
        ? selectedSlug
        : selectedSlug === "__not_sure__" ? "__not_sure__" : "";
    }

    function currentSelection() {
      const stateCode = stateSelect.value || "CA";
      const stateLabel = stateSelect.options[stateSelect.selectedIndex]?.textContent || findStateLabel(stateCode);
      const licenseSlug = licenseSelect.value === "__not_sure__" ? "" : licenseSelect.value;
      const licenseLabel = selectedLicenseLabel();
      return {
        stateCode,
        stateLabel,
        licenseSlug,
        licenseLabel,
        requestedLabel: licenseSlug ? `${stateLabel} · ${licenseLabel}` : `${stateLabel} · Not sure yet`,
        status: "waitlist",
        available: false,
        allowCheckout: false
      };
    }

    populateStateOptions(stateSelect, initialSelection.stateCode || "CA");
    populateWaitlistLicenses(initialSelection.licenseSlug || "");
    nameInput.value = [initialSelection.firstName || stored.firstName, initialSelection.lastName || stored.lastName]
      .filter(Boolean)
      .join(" ");
    emailInput.value = initialSelection.email || stored.email || "";

    loadStudyTradesOffers().then(() => {
      const selectedSlug = licenseSelect.value || initialSelection.licenseSlug || "";
      populateWaitlistLicenses(selectedSlug);
    });

    stateSelect.addEventListener("change", () => {
      populateWaitlistLicenses("");
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const name = nameInput.value.trim();
      const email = emailInput.value.trim();
      if (!name || !email || !stateSelect.value || !licenseSelect.value) {
        setStatus("Add your state, license interest, name, and email so we can save this.");
        return;
      }

      const nameParts = splitName(name);
      const selection = currentSelection();
      const payload = {
        ...selection,
        ...nameParts,
        email,
        marketingOptIn: true,
        source: params.get("source") || "shopify-waitlist-page"
      };

      setStatus("Saving...");
      if (submitButton) submitButton.disabled = true;

      try {
        if (leadEndpoint) {
          const response = await studyTradesLeadRequest(leadEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
          });
          if (!response.ok) throw new Error("Waitlist capture failed");
        }

        writeStoredSignupLead(payload);
        trackStudyTradesEvent("waitlist_submit", {
          state: payload.stateCode,
          state_name: payload.stateLabel,
          license_interest: payload.licenseSlug || "not_sure",
          source_page: window.location.pathname,
          source_section: "waitlist_page"
        });

        form.hidden = true;
        if (successCopyNode) {
          successCopyNode.textContent = payload.licenseSlug
            ? `We’ll email you when ${payload.licenseLabel} is ready in ${payload.stateLabel}.`
            : `We’ll email you when Study Trades has the right license path ready in ${payload.stateLabel}.`;
        }
        if (successNode) successNode.hidden = false;
        setStatus("");
      } catch (_error) {
        setStatus("We couldn’t save that right now. Please try again.");
      } finally {
        if (submitButton) submitButton.disabled = false;
      }
    });
  });
}

function initializeSignupIneligiblePages() {
  const pages = document.querySelectorAll("[data-signup-ineligible]");
  if (!pages.length) return;

  pages.forEach((root) => {
    const eyebrowNode = root.querySelector("[data-ineligible-eyebrow]");
    const titleNode = root.querySelector("[data-ineligible-title]");
    const bodyNode = root.querySelector("[data-ineligible-body]");
    const resourcesNode = root.querySelector("[data-ineligible-resources]");
    const applicationGuideUrl = root.getAttribute("data-application-guide-url") || "/#start-here";
    const livescanGuideUrl = root.getAttribute("data-livescan-guide-url") || "/#faq";
    const faqUrl = root.getAttribute("data-faq-url") || "/pages/faq";
    const params = new URLSearchParams(window.location.search);
    const readinessStatus = params.get("readinessStatus") || "needs-work";

    const resources =
      readinessStatus === "unsure"
        ? [
            { title: "Start with the application guide", body: "Use this to verify what you need before you pay for prep.", href: applicationGuideUrl },
            { title: "Everything about fingerprinting", body: "See the Live Scan step in plain English.", href: livescanGuideUrl },
            { title: "Read the FAQ", body: "Get quick answers before you circle back.", href: faqUrl }
          ]
        : [
            { title: "How to submit your application", body: "Handle the paperwork side first so you do not pay too early.", href: applicationGuideUrl },
            { title: "Read the FAQ", body: "Use this when you need a plain answer fast.", href: faqUrl }
          ];

    if (eyebrowNode) {
      eyebrowNode.textContent = readinessStatus === "unsure" ? "Check first" : "Not ready yet";
    }
    if (titleNode) {
      titleNode.textContent = "You're not elligible for a license yet.";
    }
    if (bodyNode) {
      bodyNode.textContent = "You flagged at least one thing that would likely block you from applying. Handle that first, then come back when the timing is right. If you have questions, here is a helpful place to read up on.";
    }
    if (resourcesNode) {
      resourcesNode.innerHTML = [
        {
          title: "Am I elligible for a license?",
          body: "Start here if you need a plain-English breakdown before you try again.",
          href: "/#start-here"
        },
        {
          title: "Read the FAQ",
          body: "See the common questions and the direct answers.",
          href: faqUrl
        }
      ].map((resource) => `
        <a class="signup-flow__resource" href="${resource.href}">
          <strong>${resource.title}</strong>
          <span>${resource.body}</span>
        </a>
      `).join("");
    }
  });
}

function initializeSignupCheckoutPages() {
  const pages = document.querySelectorAll("[data-signup-checkout]");
  if (!pages.length) return;

  pages.forEach((root) => {
    const selection = buildSignupSelection();
    const titleNode = root.querySelector("[data-checkout-title]");
    const bodyNode = root.querySelector("[data-checkout-body]");
    const licenseTitleNode = root.querySelector("[data-checkout-license-title]");
    const licenseBodyNode = root.querySelector("[data-checkout-license-body]");
    const licensePriceNode = root.querySelector("[data-checkout-license-price]");
    const lawAddonNode = root.querySelector("[data-checkout-law-addon]");
    const lawToggle = root.querySelector("[data-checkout-law-toggle]");
    const lawPriceNode = root.querySelector("[data-checkout-law-price]");
    const totalNode = root.querySelector("[data-checkout-total]");
    const primaryNode = root.querySelector("[data-checkout-primary]");
    const statusNode = root.querySelector("[data-checkout-status]");
    const pricingUrl = root.getAttribute("data-pricing-url") || "/#license-selector";
    const lawProductUrl = root.getAttribute("data-law-product-url") || "/products/california-law-and-business";
    const requiresLawOption = selection.stateCode === "CA" && selection.licenseSlug && selection.licenseSlug !== "law-business";

    let selectedVariantId = null;
    let selectedPrice = selection.priceLabel || "$250";
    let lawVariantId = null;
    let lawPrice = "$250";

    const setStatus = (message) => {
      if (statusNode) statusNode.textContent = message || "";
    };

    function moneyFromCents(cents) {
      if (typeof cents !== "number" || Number.isNaN(cents)) return "$0";
      return `$${(cents / 100).toFixed(2).replace(/\.00$/, "")}`;
    }

    function productJsonUrl(productUrl) {
      const clean = (productUrl || "").replace(/\/$/, "");
      return clean ? `${clean}.js` : "";
    }

    async function fetchProduct(productUrl) {
      const response = await fetch(productJsonUrl(productUrl), {
        headers: { Accept: "application/json" }
      });
      if (!response.ok) throw new Error("Product lookup failed");
      return response.json();
    }

    function firstAvailableVariant(product) {
      return Array.isArray(product?.variants)
        ? product.variants.find((variant) => variant.available) || product.variants[0] || null
        : null;
    }

    function updateTotals() {
      const base = selectedPrice || "$250";
      if (licensePriceNode) licensePriceNode.textContent = base;
      if (lawPriceNode) lawPriceNode.textContent = lawPrice;

      let totalCents = 0;
      const baseVariantPrice = Number(String(base).replace(/[^0-9.]/g, "")) || 0;
      totalCents += Math.round(baseVariantPrice * 100);

      if (requiresLawOption && lawToggle?.checked) {
        const lawVariantPrice = Number(String(lawPrice).replace(/[^0-9.]/g, "")) || 0;
        totalCents += Math.round(lawVariantPrice * 100);
      }

      if (totalNode) totalNode.textContent = moneyFromCents(totalCents);
    }

    if (titleNode && selection.licenseLabel) {
      titleNode.textContent = "Selected license study guide + exam prep";
    }
    if (bodyNode) {
      bodyNode.textContent = "Review what’s included, choose whether you want the optional Law and Business add-on, and then continue into secure checkout.";
    }
    if (licenseTitleNode) {
      licenseTitleNode.textContent = selection.licenseLabel
        ? `${selection.licenseLabel} study guide + exam prep`
        : "Selected license study guide + exam prep";
    }
    if (licenseBodyNode) {
      licenseBodyNode.innerHTML = [
        `<span>${escapeHtml([selection.stateLabel, selection.licenseLabel].filter(Boolean).join(" • "))}</span>`,
        "<span>• Study Materials & Multimedia Resources</span>",
        "<span>• Practice Exams</span>",
        "<span>• Guided License Checklist</span>"
      ].join("");
    }
    if (lawAddonNode) {
      lawAddonNode.hidden = !requiresLawOption;
    }

    if (lawToggle) {
      lawToggle.addEventListener("change", updateTotals);
    }

    async function hydrateCheckoutProducts() {
      try {
        if (selection.productUrl) {
          const selectedProduct = await fetchProduct(selection.productUrl);
          const variant = firstAvailableVariant(selectedProduct);
          if (variant) {
            selectedVariantId = variant.id;
            selectedPrice = moneyFromCents(variant.price);
          }
        }

        if (requiresLawOption) {
          const lawProduct = await fetchProduct(lawProductUrl);
          const variant = firstAvailableVariant(lawProduct);
          if (variant) {
            lawVariantId = variant.id;
            lawPrice = moneyFromCents(variant.price);
          }
        }
      } catch (_error) {
        setStatus("");
      }

      updateTotals();
    }

    if (primaryNode) {
      primaryNode.addEventListener("click", async () => {
        if (!selectedVariantId) {
          window.location.href = pricingUrl;
          return;
        }

        setStatus("Sending you to secure checkout...");

        try {
          await fetch("/cart/clear.js", {
            method: "POST",
            headers: { Accept: "application/json" }
          });

          const items = [{ id: selectedVariantId, quantity: 1 }];
          if (requiresLawOption && lawToggle?.checked && lawVariantId) {
            items.push({ id: lawVariantId, quantity: 1 });
          }

          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json"
            },
            body: JSON.stringify({ items })
          });

          if (!response.ok) throw new Error("Cart add failed");
          window.location.href = "/checkout";
        } catch (_error) {
          setStatus("");
          window.location.href = pricingUrl;
        }
      });
    }

    hydrateCheckoutProducts();
  });
}

function initializeLicenseSignupGateways() {
  const gateways = document.querySelectorAll("[data-license-signup]");

  gateways.forEach((root) => {
    const params = new URLSearchParams(window.location.search);
    const offers = [...studyTradesOffersCache];
    const leadEndpoint = root.getAttribute("data-lead-endpoint");
    const pricingUrl = root.getAttribute("data-pricing-url") || "/#license-selector";
    const applicationGuideUrl = root.getAttribute("data-application-guide-url") || "/#start-here";
    const livescanGuideUrl = root.getAttribute("data-livescan-guide-url") || "/#faq";
    const faqUrl = root.getAttribute("data-faq-url") || "/pages/faq";
    const blogUrl = root.getAttribute("data-blog-url") || "/#blogs";

    const titleNode = root.querySelector("[data-signup-selection]");
    const headerCopyNode = root.querySelector("[data-signup-header-copy]");
    const detailNode = root.querySelector("[data-signup-detail]");
    const preorderDiscountNode = root.querySelector("[data-signup-preorder-discount]");
    const statePill = root.querySelector("[data-signup-state-pill]");
    const licensePill = root.querySelector("[data-signup-license-pill]");
    const progressSteps = Array.from(root.querySelectorAll(".license-signup__progress-step"));
    const stepNodes = Array.from(root.querySelectorAll("[data-signup-step]"));
    const stateSelect = root.querySelector("[data-signup-state-select]");
    const licenseSelect = root.querySelector("[data-signup-license-select]");
    const licenseField = root.querySelector("[data-signup-license-field]");
    const licenseMenu = root.querySelector("[data-signup-license-menu]");
    const licenseSummary = root.querySelector("[data-signup-license-summary]");
    const licenseOptions = root.querySelector("[data-signup-license-options]");
    const stateNote = root.querySelector("[data-signup-state-note]");
    const licenseNote = root.querySelector("[data-signup-license-note]");
    const firstNameInput = root.querySelector("[data-signup-first-name]");
    const lastNameInput = root.querySelector("[data-signup-last-name]");
    const emailInput = root.querySelector("[data-signup-email]");
    const phoneInput = root.querySelector("[data-signup-phone]");
    const consentInput = root.querySelector("[data-signup-consent]");
    const consentCopy = root.querySelector("[data-signup-consent-copy]");
    const contactAction = root.querySelector("[data-signup-contact-action]");
    const selectionContinueButton = root.querySelector("[data-signup-selection-continue]");
    const continueButton = root.querySelector("[data-signup-continue]");
    const profileNext = root.querySelector("[data-signup-profile-next]");
    const evaluateButton = root.querySelector("[data-signup-evaluate]");
    const backButton = root.querySelector("[data-signup-back-to-lead]");
    const backToSelectionButton = root.querySelector("[data-signup-back-to-selection]");
    const waitlistOptInButton = root.querySelector("[data-signup-waitlist-optin]");
    const waitlistOptInLabel = root.querySelector("[data-signup-waitlist-optin-label]");
    const waitlistDeclineButton = root.querySelector("[data-signup-waitlist-decline]");
    const waitlistTitle = root.querySelector("[data-signup-waitlist-title]");
    const waitlistCopy = root.querySelector("[data-signup-waitlist-copy]");
    const statusNode = root.querySelector("[data-signup-status]");
    const resultEyebrow = root.querySelector("[data-signup-result-eyebrow]");
    const resultTitle = root.querySelector("[data-signup-result-title]");
    const resultBody = root.querySelector("[data-signup-result-body]");
    const resultPrimary = root.querySelector("[data-signup-result-primary]");
    const resultSecondary = root.querySelector("[data-signup-result-secondary]");
    const resultCard = root.querySelector("[data-signup-result-card]");
    const resourceList = root.querySelector("[data-signup-resource-list]");

    if (!stateSelect || !licenseSelect || !firstNameInput || !lastNameInput || !emailInput || !continueButton) return;

    offers.push(...studyTradesOffersCache);

    const referredLicense = params.get("licenseSlug") ? null : licenseFromSignupReferrer();

    let savedLead = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      stateCode: params.get("stateCode") || "CA",
      stateLabel: params.get("stateLabel") || "California",
      licenseSlug: params.get("licenseSlug") || referredLicense?.slug || STUDY_TRADES_PRIMARY_LICENSE_SLUG,
      licenseLabel: params.get("licenseLabel") || referredLicense?.label || STUDY_TRADES_PRIMARY_LICENSE_LABEL,
      selectedLicenses: [],
      status: params.get("status") || "waitlist",
      allowCheckout: params.get("allowCheckout") === "1",
      requestedLabel: params.get("stateLabel") || "California"
    };

    const resourceSets = {
      waitlist: (stateLabel) => [
        {
          title: `What to know before ${stateLabel} launches`,
          description: "Keep moving with plain-language licensing guidance while we finish the rollout.",
          href: blogUrl
        },
        {
          title: "Frequently asked questions",
          description: "Straight answers to the questions people ask before they commit.",
          href: faqUrl
        }
      ],
      ready: [
        {
          title: "What to do before you submit your application",
          description: "Use this if you want the application side to go smoothly from day one.",
          href: applicationGuideUrl
        },
        {
          title: "Need the bigger picture first?",
          description: "Browse the Study Trades blog before you jump into checkout.",
          href: blogUrl
        }
      ],
      needsWork: [
        {
          title: "How to submit your application",
          description: "A practical walkthrough of what you need before you send paperwork to the CSLB.",
          href: applicationGuideUrl
        },
        {
          title: "Frequently asked questions",
          description: "Use this when you need a plain answer fast.",
          href: faqUrl
        }
      ],
      unsure: [
        {
          title: "Start with the application guide",
          description: "This is the best first pass if you still need to verify what applies to you.",
          href: applicationGuideUrl
        },
        {
          title: "Everything you need to know about Live Scan",
          description: "Use this once you’re into the process and want the next off-platform step made simple.",
          href: livescanGuideUrl
        },
        {
          title: "Study Trades blog",
          description: "See the resource hub if you want more context before paying.",
          href: blogUrl
        }
      ]
    };

    function setStatus(message) {
      if (statusNode) statusNode.textContent = message || "";
    }

    function setBusy(isBusy) {
      root.classList.toggle("is-loading", Boolean(isBusy));
      [selectionContinueButton, profileNext, continueButton, evaluateButton, waitlistOptInButton, waitlistDeclineButton].forEach((button) => {
        if (button) button.disabled = Boolean(isBusy);
      });
    }

    function selectedLicenses() {
      return Array.from(licenseSelect.selectedOptions).map((option) => ({
        slug: option.value,
        label: option.textContent
      })).filter((license) => license.slug);
    }

    function markUnavailableStates() {
      Array.from(stateSelect.options).forEach((option) => {
        const label = findStateLabel(option.value);
        option.textContent = option.value === STUDY_TRADES_PRIMARY_STATE ? label : `${label} *`;
      });
    }

    function syncLicenseAvailability() {
      const isPrimaryState = stateSelect.value === STUDY_TRADES_PRIMARY_STATE;
      licenseSelect.disabled = !isPrimaryState;
      licenseField?.classList.toggle("is-disabled", !isPrimaryState);
      if (stateNote) stateNote.hidden = isPrimaryState;
      if (licenseNote) licenseNote.hidden = isPrimaryState;

      if (!isPrimaryState) {
        Array.from(licenseSelect.options).forEach((option) => {
          option.selected = false;
        });
        if (!licenseSelect.multiple) licenseSelect.value = "";
      }
    }

    function currentSelection() {
      const currentStateCode = stateSelect.value || savedLead.stateCode || "CA";
      const currentStateLabel = findStateLabel(currentStateCode);
      const licenses = selectedLicenses();
      const currentLicenseSlug = licenses[0]?.slug || "";
      const currentLicenseLabel = licenses[0]?.label || "";
      const selectionOffer = findOfferForSelection(offers, currentStateCode, currentLicenseSlug);
      const status = selectionOffer?.status || savedLead.status || (selectionOffer?.available ? "live" : "waitlist");

      return {
        stateCode: currentStateCode,
        stateLabel: currentStateLabel,
        licenseSlug: currentLicenseSlug,
        licenseLabel: currentLicenseLabel || "Select your license",
        selectedLicenses: licenses,
        requestedLabel: licenses.length
          ? `${currentStateLabel} · ${licenses.map((license) => license.label).join(", ")}`
          : currentStateLabel,
        offer: selectionOffer,
        status,
        available: status === "live",
        allowCheckout: offerCanCheckout(selectionOffer),
        priceLabel: selectionOffer?.priceLabel || "",
        productUrl: selectionOffer?.productUrl || productUrlForSelection(currentStateCode, currentLicenseSlug)
      };
    }

    function syncSavedLeadSelection() {
      const selection = currentSelection();
      savedLead = {
        ...savedLead,
        stateCode: selection.stateCode,
        stateLabel: selection.stateLabel,
        licenseSlug: selection.licenseSlug,
        licenseLabel: selection.licenseLabel === "Select your license" ? "" : selection.licenseLabel,
        selectedLicenses: selection.selectedLicenses,
        status: selection.status,
        allowCheckout: selection.allowCheckout,
        requestedLabel: selection.requestedLabel
      };
      return selection;
    }

    function showStep(stepName) {
      const selection = syncSavedLeadSelection();
      stepNodes.forEach((node) => {
        node.hidden = node.getAttribute("data-signup-step") !== stepName;
      });

      root.classList.toggle("is-waitlist-step", stepName === "waitlist");
      root.classList.toggle("is-preorder-step", stepName === "waitlist" && offerIsPreorder(selection.offer));
      if (preorderDiscountNode) {
        preorderDiscountNode.hidden = !(stepName === "waitlist" && offerIsPreorder(selection.offer));
      }

      if (titleNode) {
        titleNode.textContent = stepName === "selection"
          ? "Which State and License(s) are you interested in?"
          : stepName === "contact" && selection.stateCode !== STUDY_TRADES_PRIMARY_STATE
            ? `We're almost ready to launch in ${selection.stateLabel}.`
            : stepName === "contact"
              ? "Choose your test prep"
              : stepName === "profile"
                ? "Create your account"
              : stepName === "readiness"
                ? "Let's check your eligibility"
                : stepName === "waitlist" && offerIsPreorder(selection.offer)
                  ? "This course is in pre-order, but here's why that's good:"
                  : stepName === "thanks"
                    ? "Thanks for your interest."
                    : "Your next step";
      }
      if (headerCopyNode) {
        const showLaunchCopy = stepName === "contact" && selection.stateCode !== STUDY_TRADES_PRIMARY_STATE;
        headerCopyNode.hidden = !showLaunchCopy;
        headerCopyNode.textContent = showLaunchCopy
          ? "Add your information below to receive a pre-launch discount code."
          : "";
      }

      let activeIndex = 0;
      if (stepName === "profile") activeIndex = 1;
      if (stepName === "readiness" || stepName === "waitlist" || stepName === "result" || stepName === "thanks") activeIndex = 2;

      progressSteps.forEach((step, index) => {
        step.classList.toggle("is-active", index === activeIndex);
        step.classList.toggle("is-complete", index < activeIndex);
      });
    }

    function renderResources(resources) {
      if (!resourceList) return;
      resourceList.innerHTML = resources.map((resource) => `
        <a class="license-signup__resource" href="${resource.href}">
          <strong>${resource.title}</strong>
          <span>${resource.description}</span>
        </a>
      `).join("");
    }

    async function sendLead(payload) {
      if (!leadEndpoint) return { ok: true };
      const response = await studyTradesLeadRequest(leadEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error("Lead capture failed");
      }
      return response.json();
    }

    async function captureLead(payload) {
      try {
        await sendLead(payload);
        return true;
      } catch (error) {
        console.warn("Study Trades lead capture failed", error);
        return false;
      }
    }

    function rememberLead(extraPayload = {}) {
      const selection = syncSavedLeadSelection();
      try {
        window.localStorage.setItem("studyTradesSignupEmail", savedLead.email);
        window.localStorage.setItem(
          "studyTradesSignupLicense",
          JSON.stringify({
            ...savedLead,
            stateCode: selection.stateCode,
            stateLabel: selection.stateLabel,
            licenseSlug: selection.licenseSlug,
            licenseLabel: selection.licenseLabel,
            requestedLabel: selection.requestedLabel,
            ...extraPayload
          })
        );
      } catch (_storageError) {
        // Ignore local storage failures.
      }
    }

    function selectedApplicationStage() {
      const educationValue = root.querySelector("input[name='signup-education']:checked")?.value || "";
      return educationValue || null;
    }

    function normalizeReadiness() {
      const ageValue = root.querySelector("input[name='signup-age']:checked")?.value || "";
      const idValue = root.querySelector("input[name='signup-id']:checked")?.value || "";
      const educationValue = selectedApplicationStage();
      const experienceValue = root.querySelector("input[name='signup-experience']:checked")?.value || "";

      if (!ageValue || !idValue || !educationValue || !experienceValue) return null;

      let readinessStatus = "unsure";
      if (ageValue === "yes" && idValue === "yes" && experienceValue === "more-than-4") {
        readinessStatus = "ready";
      } else if (ageValue === "no" || idValue === "no" || (educationValue === "on-the-job" && experienceValue === "less-than-4")) {
        readinessStatus = "needs-work";
      }

      return {
        ageConfirmed: ageValue === "yes",
        tradeEducation: educationValue,
        experienceStatus: experienceValue,
        ssnOrItinConfirmed: idValue === "yes" ? true : idValue === "no" ? false : null,
        readinessStatus
      };
    }

    function syncSelectionCopy() {
      const selection = syncSavedLeadSelection();

      if (detailNode) {
        detailNode.textContent = "";
      }

      if (statePill) statePill.textContent = selection.stateLabel;
      if (licensePill) {
        licensePill.textContent = selection.licenseSlug ? selection.licenseLabel : "Select your license";
      }
      if (waitlistTitle) {
        waitlistTitle.textContent = selection.stateCode !== STUDY_TRADES_PRIMARY_STATE
          ? `We're still finalizing our program for ${selection.stateLabel}.`
          : offerIsPreorder(selection.offer)
            ? ""
            : "Join the waitlist.";
      }
      if (waitlistCopy) {
        waitlistCopy.textContent = selection.stateCode !== STUDY_TRADES_PRIMARY_STATE
          ? "We'll email you when that launch happens."
          : offerIsPreorder(selection.offer)
            ? `${selection.licenseLabel} is in the final stages of testing and will be live and ready in a few weeks or less. The good news for you is that if you choose to sign up today, you will receive an early bird discount of 50% as a thank you for waiting until it goes live.`
            : `We'll email you when ${selection.licenseLabel} opens.`;
      }
      if (waitlistOptInLabel) {
        waitlistOptInLabel.textContent = selection.allowCheckout ? "Next" : "Join the waitlist";
      }
      if (waitlistDeclineButton) {
        waitlistDeclineButton.setAttribute(
          "aria-label",
          selection.stateCode !== STUDY_TRADES_PRIMARY_STATE ? "Back" : "Change selection"
        );
      }
      if (consentCopy) {
        consentCopy.textContent = selection.stateCode === STUDY_TRADES_PRIMARY_STATE
          ? "Send me study tips and product updates (optional)."
          : `I agree to receive emails related to Study Trades launching in ${selection.stateLabel}.`;
      }
      if (contactAction) {
        contactAction.textContent = selection.stateCode === STUDY_TRADES_PRIMARY_STATE ? "Create account" : "Join waitlist";
      }
    }

    function renderLicenseMultiSelect(selectedSlugs = []) {
      if (!licenseOptions) {
        const selected = selectedSlugs.find(Boolean) || "";
        if (selected && Array.from(licenseSelect.options).some((option) => option.value === selected)) {
          licenseSelect.value = selected;
        }
        syncLicenseAvailability();
        licenseField?.classList.toggle("is-error", !licenseSelect.value && licenseField.classList.contains("has-validation-error"));
        return;
      }

      const selected = new Set(selectedSlugs.filter(Boolean));
      Array.from(licenseSelect.options).forEach((option) => {
        option.selected = selected.has(option.value);
      });

      licenseOptions.innerHTML = Array.from(licenseSelect.options)
        .filter((option) => option.value)
        .map((option) => `
          <label>
            <input type="checkbox" value="${escapeHtml(option.value)}" ${selected.has(option.value) ? "checked" : ""}>
            <span>${escapeHtml(option.textContent)}</span>
          </label>
        `).join("");

      const updateSummary = () => {
        const licenses = selectedLicenses();
        if (licenseSummary) {
          licenseSummary.textContent = licenses.length === 0
            ? "Select license(s)"
            : licenses.length === 1
              ? licenses[0].label
              : `${licenses.length} licenses selected`;
        }
        licenseField?.classList.toggle("is-error", licenses.length === 0 && licenseField.classList.contains("has-validation-error"));
      };

      licenseOptions.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
        checkbox.addEventListener("change", () => {
          const option = Array.from(licenseSelect.options).find((item) => item.value === checkbox.value);
          if (option) option.selected = checkbox.checked;
          licenseField?.classList.remove("is-error", "has-validation-error");
          updateSummary();
          syncSelectionCopy();
          setStatus("");
        });
      });
      updateSummary();
      syncLicenseAvailability();
    }

    function hydrateLicenseChoices(selectedStateCode, selectedLicenseSlug, selectedSlugs = []) {
      populateStateOptions(stateSelect, selectedStateCode);
      markUnavailableStates();
      const isPrimaryState = stateSelect.value === STUDY_TRADES_PRIMARY_STATE;
      const choices = isPrimaryState ? getLicenseChoicesForState(stateSelect.value, offers) : [];
      populateLicenseOptions(
        licenseSelect,
        choices,
        isPrimaryState ? selectedLicenseSlug : "",
        isPrimaryState ? "Select a California license" : "Select California first"
      );
      renderLicenseMultiSelect(selectedSlugs.length ? selectedSlugs : [selectedLicenseSlug]);
      syncSelectionCopy();
    }

    function renderResult(readinessStatus) {
      if (!resultEyebrow || !resultTitle || !resultBody || !resultPrimary || !resultSecondary || !resultCard) return;
      const selection = syncSavedLeadSelection();

      resultCard.classList.remove("is-ready", "is-needs-work", "is-unsure");

      if (!selection.allowCheckout) {
        resultCard.classList.add("is-unsure");
        resultEyebrow.textContent = "Waitlist";
        resultTitle.textContent = selection.stateCode !== STUDY_TRADES_PRIMARY_STATE
          ? `We're still finalizing our program for ${selection.stateLabel}.`
          : "You're on the waitlist.";
        resultBody.textContent = selection.stateCode !== STUDY_TRADES_PRIMARY_STATE
          ? `We saved your interest in ${selection.stateLabel}. We'll email you when Study Trades launches in your state.`
          : `We saved your interest in ${selection.licenseLabel}. You'll receive an early bird discount when this course opens.`;
        resultPrimary.textContent = "Back to home";
        resultPrimary.setAttribute("href", "/");
        resultSecondary.hidden = false;
        resultSecondary.textContent = "Read the FAQ";
        resultSecondary.setAttribute("href", faqUrl);
        renderResources(resourceSets.waitlist(selection.stateLabel));
        showStep("result");
        return;
      }

      if (readinessStatus === "ready") {
        resultCard.classList.add("is-ready");
        resultEyebrow.textContent = "Good to go";
        resultTitle.textContent = "You look ready to start test prep.";
        resultBody.textContent = "Nothing here guarantees CSLB approval, but your answers fit the basic profile for starting exam prep.";
        resultPrimary.textContent = "Continue to payment";
        resultPrimary.setAttribute("href", selection.productUrl || pricingUrl);
        resultSecondary.hidden = false;
        resultSecondary.textContent = "Review the basics first";
        resultSecondary.setAttribute("href", applicationGuideUrl);
        renderResources(resourceSets.ready);
      } else if (readinessStatus === "needs-work") {
        resultCard.classList.add("is-needs-work");
        resultEyebrow.textContent = "Not yet";
        resultTitle.textContent = "You're not ready to study for your exam yet.";
        resultBody.textContent = "First, fulfill the requirements set by the CSLB. Once those basics are handled, come back and start your test prep.";
        resultPrimary.textContent = "Review CSLB requirements";
        resultPrimary.setAttribute("href", CSLB_REQUIREMENTS_URL);
        resultSecondary.hidden = false;
        resultSecondary.textContent = "Read the FAQ";
        resultSecondary.setAttribute("href", faqUrl);
        renderResources(resourceSets.needsWork);
      } else {
        resultCard.classList.add("is-unsure");
        resultEyebrow.textContent = "Worth checking first";
        resultTitle.textContent = "You need a little more clarity before paying.";
        resultBody.textContent = "You’re probably close, but one or more answers still need to be verified. Use the guides below, then come back once you know where you stand.";
        resultPrimary.textContent = "Start with the application guide";
        resultPrimary.setAttribute("href", applicationGuideUrl);
        resultSecondary.hidden = false;
        resultSecondary.textContent = "Read the Live Scan guide";
        resultSecondary.setAttribute("href", livescanGuideUrl);
        renderResources(resourceSets.unsure);
      }

      showStep("result");
    }

    const boot = async () => {
      try {
        hydrateLicenseChoices(savedLead.stateCode, savedLead.licenseSlug, savedLead.selectedLicenses.map((license) => license.slug));
        firstNameInput.value = savedLead.firstName || "";
        lastNameInput.value = savedLead.lastName || "";
        emailInput.value = savedLead.email || "";
        if (phoneInput) phoneInput.value = savedLead.phone || "";
        showStep("contact");
      } finally {
        root.classList.remove("is-initializing");
        root.classList.remove("is-loading");
      }
      // Prices/launch readiness update in the background, never replacing the
      // user's current step or typed values. The static classification list is
      // sufficient for immediate navigation and account registration.
      try {
        const latestOffers = await loadStudyTradesOffers();
        offers.splice(0, offers.length, ...latestOffers);
        const selected=syncSavedLeadSelection();
        hydrateLicenseChoices(selected.stateCode, selected.licenseSlug, selected.selectedLicenses.map(license=>license.slug));
      } catch { /* Keep the bundled catalog usable during offer-service outages. */ }
    };

    boot();

    stateSelect.addEventListener("change", () => {
      markUnavailableStates();
      const isPrimaryState = stateSelect.value === STUDY_TRADES_PRIMARY_STATE;
      const choices = isPrimaryState ? getLicenseChoicesForState(stateSelect.value, offers) : [];
      populateLicenseOptions(
        licenseSelect,
        choices,
        "",
        isPrimaryState ? "Select a California license" : "Select California first"
      );
      renderLicenseMultiSelect([]);
      syncSelectionCopy();
      setStatus("");
    });

    function setFieldError(field, hasError) {
      field?.closest("label")?.classList.toggle("is-error", Boolean(hasError));
    }

    [firstNameInput, lastNameInput, emailInput, stateSelect, licenseSelect].forEach((field) => {
      field.addEventListener("input", () => setFieldError(field, false));
      field.addEventListener("change", () => setFieldError(field, false));
    });

    selectionContinueButton?.addEventListener("click", () => {
      setStatus("");
      showStep("contact");
    });

    profileNext?.addEventListener("click", () => {
      const selection = syncSavedLeadSelection();
      const validEmail = emailInput.value.trim() && emailInput.checkValidity();
      setFieldError(emailInput, !validEmail);
      setFieldError(licenseSelect, selection.stateCode === STUDY_TRADES_PRIMARY_STATE && !selection.licenseSlug);
      if (!validEmail || (selection.stateCode === STUDY_TRADES_PRIMARY_STATE && !selection.licenseSlug)) {
        setStatus("Enter your email and choose a license classification."); return;
      }
      savedLead.email = emailInput.value.trim().toLowerCase();
      setStatus(""); showStep("profile");
    });
    continueButton.addEventListener("click", async () => {
      const firstName = firstNameInput.value.trim();
      const lastName = lastNameInput.value.trim();
      const email = emailInput.value.trim().toLowerCase();
      const phone = phoneInput?.value.trim() || "";
      const selection = syncSavedLeadSelection();
      const fieldErrors = [
        [firstNameInput, !firstName],
        [lastNameInput, !lastName],
        [emailInput, !email],
        [licenseSelect, selection.stateCode === STUDY_TRADES_PRIMARY_STATE && !selection.licenseSlug]
      ];

      fieldErrors.forEach(([field, hasError]) => setFieldError(field, hasError));

      if (!firstName || !lastName || !email || (selection.stateCode === STUDY_TRADES_PRIMARY_STATE && !selection.licenseSlug)) {
        setStatus(selection.stateCode === STUDY_TRADES_PRIMARY_STATE
          ? "Choose your California license classification and add your contact details."
          : "Add your name and email so we can notify you when your state launches.");
        return;
      }

      savedLead.firstName = firstName;
      savedLead.lastName = lastName;
      savedLead.email = email;
      savedLead.phone = phone;

      setStatus("");
      setBusy(true);

      try {
        const registration = {
          firstName,
          lastName,
          email,
          phone,
          stateCode: selection.stateCode,
          stateLabel: selection.stateLabel,
          licenseSlug: selection.licenseSlug,
          licenseLabel: selection.licenseLabel,
          selectedLicenses: selection.selectedLicenses,
          requestedLabel: selection.requestedLabel,
          marketingOptIn: Boolean(consentInput?.checked),
          source: selection.available ? "shopify-signup-partial" : "shopify-waitlist-partial"
        };
        if (selection.stateCode === STUDY_TRADES_PRIMARY_STATE) {
          const response = await studyTradesLeadRequest(new URL('/api/auth/register', leadEndpoint).href, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(registration)
          });
          const result = await response.json();
          if (!response.ok || !result.ok) throw new Error(result.error || 'Account setup could not be completed. Please try again.');
        } else if (!await captureLead(registration)) throw new Error('We could not save your request. Please try again.');
        rememberLead({ selectedLicenses: selection.selectedLicenses, marketingOptIn: Boolean(consentInput?.checked) });
        setStatus("");
        if (selection.stateCode !== STUDY_TRADES_PRIMARY_STATE) {
          showStep("thanks");
        } else if (selection.available) {
          showStep("readiness");
        } else if (selection.stateCode === STUDY_TRADES_PRIMARY_STATE && selection.allowCheckout) {
          showStep("waitlist");
        } else {
          renderResult("waitlist");
        }
      } catch (error) {
        setStatus(error instanceof Error ? error.message : 'Account setup could not be completed. Please try again.');
      } finally {
        setBusy(false);
      }
    });

    consentInput?.addEventListener("change", () => {
      consentInput.closest("label")?.classList.remove("is-error");
      setStatus("");
    });

    backToSelectionButton?.addEventListener("click", () => {
      setStatus("");
      showStep("contact");
    });

    if (backButton) {
      backButton.addEventListener("click", () => {
        setStatus("");
        showStep("contact");
      });
    }

    if (evaluateButton) {
      evaluateButton.addEventListener("click", async () => {
        const readiness = normalizeReadiness();
        if (!readiness) {
          setStatus("Answer all four questions first so we can point you to the right next step.");
          return;
        }

        setStatus("");
        setBusy(true);

        try {
          const selection = syncSavedLeadSelection();
          await captureLead({
            firstName: savedLead.firstName,
            lastName: savedLead.lastName,
            email: savedLead.email,
            phone: savedLead.phone,
            stateCode: selection.stateCode,
            stateLabel: selection.stateLabel,
            licenseSlug: selection.licenseSlug,
            licenseLabel: selection.licenseLabel,
            requestedLabel: selection.requestedLabel,
            marketingOptIn: false,
            source: selection.available ? "shopify-signup-eligibility" : "shopify-waitlist-eligibility",
            ...readiness
          });
          rememberLead(readiness);
          setStatus("");
          if (readiness.readinessStatus === "ready" && selection.allowCheckout) {
            window.location.href = selection.productUrl || pricingUrl;
            return;
          }
          renderResult(selection.allowCheckout ? readiness.readinessStatus : "waitlist");
        } finally {
          setBusy(false);
        }
      });
    }

    if (waitlistOptInButton) {
      waitlistOptInButton.addEventListener("click", async () => {
        const selection = syncSavedLeadSelection();
        if (selection.stateCode === STUDY_TRADES_PRIMARY_STATE && selection.allowCheckout) {
          setStatus("");
          setBusy(true);
          try {
            await captureLead({
              firstName: savedLead.firstName,
              lastName: savedLead.lastName,
              email: savedLead.email,
              phone: savedLead.phone,
              stateCode: selection.stateCode,
              stateLabel: selection.stateLabel,
              licenseSlug: selection.licenseSlug,
              licenseLabel: selection.licenseLabel,
              requestedLabel: selection.requestedLabel,
              marketingOptIn: true,
              source: "shopify-license-waitlist-acknowledged"
            });
            rememberLead({ marketingOptIn: true, waitlistAcknowledged: true });
            setStatus("");
            showStep("readiness");
          } finally {
            setBusy(false);
          }
          return;
        }

        setStatus("");
        setBusy(true);
        try {
          await captureLead({
            firstName: savedLead.firstName,
            lastName: savedLead.lastName,
            email: savedLead.email,
            phone: savedLead.phone,
            stateCode: selection.stateCode,
            stateLabel: selection.stateLabel,
            licenseSlug: selection.licenseSlug,
            licenseLabel: selection.licenseLabel,
            requestedLabel: selection.requestedLabel,
            marketingOptIn: true,
            source: "shopify-state-waitlist"
          });
          rememberLead({ marketingOptIn: true });
          setStatus("");
          renderResult("waitlist");
        } finally {
          setBusy(false);
        }
      });
    }

    if (waitlistDeclineButton) {
      waitlistDeclineButton.addEventListener("click", () => {
        setStatus("");
        showStep("contact");
      });
    }

  });
}

function initializeLeadCaptureForms() {
  const forms = document.querySelectorAll("[data-lead-capture-form]");

  forms.forEach((form) => {
    const endpoint = form.getAttribute("data-lead-endpoint");
    const source = form.getAttribute("data-lead-source") || "blog-subscribe";
    const stateCode = form.getAttribute("data-lead-state") || "CA";
    const licenseSlug = form.getAttribute("data-lead-license") || "";
    const requestedLabel = form.getAttribute("data-lead-label") || "";
    const successMessage =
      form.getAttribute("data-lead-success") || "You’re in. We’ll send practical licensing updates and new Study Trades guides.";
    const statusNode = form.parentElement?.querySelector("[data-lead-status]");

    form.addEventListener("submit", async (event) => {
      if (!endpoint) return;
      event.preventDefault();

      const formData = new FormData(form);
      const email = String(formData.get("email") || "").trim();
      if (!email) {
        if (statusNode) statusNode.textContent = "Enter your email so we know where to send updates.";
        return;
      }

      if (statusNode) statusNode.textContent = "Saving your email...";

      try {
        const response = await studyTradesLeadRequest(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            source,
            stateCode,
            licenseSlug: licenseSlug || null,
            requestedLabel: requestedLabel || null,
            marketingOptIn: true
          })
        });
        if (!response.ok) throw new Error("Lead capture failed");
        form.reset();
        if (statusNode) statusNode.textContent = successMessage;
      } catch (_error) {
        if (statusNode) statusNode.textContent = "We couldn’t save that right now. Please try again in a moment.";
      }
    });
  });
}

function getFrankConfig() {
  return window.STUDY_TRADES_FRANK_CONFIG || null;
}

function readFrankState() {
  try {
    return JSON.parse(window.sessionStorage.getItem("studyTradesFrankState") || "{}");
  } catch (_error) {
    return {};
  }
}

function writeFrankState(values) {
  const next = { ...readFrankState(), ...values };
  try {
    window.sessionStorage.setItem("studyTradesFrankState", JSON.stringify(next));
  } catch (_error) {
    // Ignore storage failures.
  }
  return next;
}

function clearFrankState() {
  try {
    window.sessionStorage.removeItem("studyTradesFrankState");
  } catch (_error) {
    // Ignore storage failures.
  }
}

function frankHistoryFromState(state) {
  return Array.isArray(state?.history) ? state.history : [];
}

function shouldShowFrank(config) {
  const path = window.location.pathname || "/";
  if (path === "/pages/signup" || path.startsWith("/pages/signup/")) return false;
  return (config.showOnPaths || []).some((candidate) => {
    if (candidate === "/") return path === "/";
    return path === candidate || path.startsWith(`${candidate}/`);
  });
}

function deriveFrankSegmentClient({ stateCode, available, readinessStatus }) {
  const normalizedState = (stateCode || "").trim().toUpperCase();
  if (!normalizedState) return "general_lead";
  if (normalizedState !== "CA") return "out_of_state_waitlist";
  if (!available) return "ca_waitlist_unlaunched";
  if (readinessStatus === "ready") return "ca_ready";
  if (readinessStatus === "needs-work") return "ca_needs_work";
  if (readinessStatus === "unsure") return "ca_unsure";
  return "general_lead";
}

function computeFrankReadiness(answers) {
  if (answers.experienceStatus === "building") return "needs-work";
  if (answers.experienceStatus === "qualified" && answers.goalStatus === "prep_now" && answers.timelineStatus === "soon") return "ready";
  return "unsure";
}

function initializeFrankAssistant() {
  const config = getFrankConfig();
  if (!config || !shouldShowFrank(config) || document.querySelector("[data-frank-widget]")) return;

  const widget = document.createElement("section");
  widget.className = "frank-widget";
  widget.setAttribute("data-frank-widget", "");
  widget.innerHTML = `
    <button class="frank-widget__launcher" type="button" data-frank-launcher aria-expanded="false" aria-label="${escapeHtml(config.launcherLabel || "Ask Frank")}">
      <span class="frank-widget__launcher-mark">F</span>
    </button>
    <div class="frank-widget__panel" data-frank-panel hidden>
      <div class="frank-widget__header">
        <div class="frank-widget__identity">
          <span class="frank-widget__avatar">F</span>
          <div>
            <strong>Frank</strong>
            <p>Usually replies instantly</p>
          </div>
        </div>
        <div class="frank-widget__header-actions">
          <button type="button" class="frank-widget__close" data-frank-close aria-label="${escapeHtml(config.closeLabel || "Close Frank")}">
            <span class="frank-widget__close-chevron" aria-hidden="true">⌄</span>
          </button>
        </div>
      </div>
      <div class="frank-widget__body" data-frank-body></div>
    </div>
  `;

  document.body.appendChild(widget);

  const launcher = widget.querySelector("[data-frank-launcher]");
  const panel = widget.querySelector("[data-frank-panel]");
  const body = widget.querySelector("[data-frank-body]");
  const closeButton = widget.querySelector("[data-frank-close]");

  let offers = [];
  let readinessStepIndex = 0;
  let readinessAnswers = {};
  let frankState = readFrankState();

  const endpointBase = "https://app.studytrades.com/api";
  const marketingLeadEndpoint = `${endpointBase}/marketing/lead`;
  const licenseInterestEndpoint = `${endpointBase}/license-interest`;

  function currentSelection() {
    const stateCode = frankState.stateCode || "CA";
    const licenseSlug = frankState.licenseSlug || "";
    const stateLabel = frankState.stateLabel || findStateLabel(stateCode);
    const licenseLabel =
      frankState.licenseLabel ||
      getLicenseChoicesForState(stateCode, offers).find((item) => item.slug === licenseSlug)?.label ||
      "";
    const offer = licenseSlug ? findOfferForSelection(offers, stateCode, licenseSlug) : null;
    const available = typeof frankState.available === "boolean" ? frankState.available : Boolean(offer?.available);
    const productUrl = offer?.productUrl || productUrlForSelection(stateCode, licenseSlug) || config.pricingUrl;
    return {
      stateCode,
      stateLabel,
      licenseSlug,
      licenseLabel,
      requestedLabel: licenseLabel ? `${stateLabel} · ${licenseLabel}` : stateLabel,
      available,
      productUrl
    };
  }

  function resetFrankConversation(preserve = {}) {
    frankState = writeFrankState({ ...preserve, history: [] });
  }

  function appendFrankMessage(role, text) {
    if (!text) return;
    const history = frankHistoryFromState(frankState);
    const last = history[history.length - 1];
    if (last && last.role === role && last.text === text) return;
    frankState = writeFrankState({
      history: [...history, { role, text }]
    });
  }

  function openFrank() {
    if (!body.innerHTML.trim()) {
      renderHome();
    }
    panel.hidden = false;
    widget.classList.add("is-open");
    launcher.setAttribute("aria-expanded", "true");
  }

  function closeFrank() {
    panel.hidden = true;
    widget.classList.remove("is-open");
    launcher.setAttribute("aria-expanded", "false");
  }

  function setView(html) {
    const history = frankHistoryFromState(frankState);
    body.innerHTML = `
      <div class="frank-widget__view">
        <div class="frank-widget__transcript">
          ${history
            .map((entry) =>
              entry.role === "assistant"
                ? `
                  <div class="frank-widget__row frank-widget__row--assistant">
                    <span class="frank-widget__bubble-avatar">F</span>
                    <div class="frank-widget__bubble-stack">
                      <div class="frank-widget__message frank-widget__message--assistant"><p>${escapeHtml(entry.text)}</p></div>
                    </div>
                  </div>
                `
                : `
                  <div class="frank-widget__row frank-widget__row--user">
                    <div class="frank-widget__bubble-stack frank-widget__bubble-stack--user">
                      <div class="frank-widget__message frank-widget__message--user"><p>${escapeHtml(entry.text)}</p></div>
                    </div>
                  </div>
                `
            )
            .join("")}
          <div class="frank-widget__composer">
            ${html}
          </div>
        </div>
      </div>
    `;
    body.scrollTop = body.scrollHeight;
  }

  function renderHome() {
    const lastSelection = currentSelection();
    const canResume = Boolean(lastSelection.licenseSlug || frankState.currentIntent);
    resetFrankConversation({
      stateCode: frankState.stateCode,
      stateLabel: frankState.stateLabel,
      licenseSlug: frankState.licenseSlug,
      licenseLabel: frankState.licenseLabel,
      requestedLabel: frankState.requestedLabel,
      available: frankState.available,
      firstName: frankState.firstName,
      email: frankState.email,
      experienceStatus: frankState.experienceStatus,
      readinessStatus: frankState.readinessStatus
    });
    appendFrankMessage("assistant", config.intro);
    setView(
      `
        <div class="frank-widget__actions">
          ${(config.intents || [])
            .map(
              (intent) => `
                <button type="button" class="frank-widget__chip" data-frank-intent="${escapeHtml(intent.id)}">
                  ${escapeHtml(intent.label)}
                </button>
              `
            )
            .join("")}
        </div>
        ${
          canResume
            ? `
              <button type="button" class="frank-widget__secondary" data-frank-resume>
                Continue with ${escapeHtml(lastSelection.licenseLabel || lastSelection.stateLabel || "my last step")}
              </button>
            `
            : ""
        }
      `
    );
  }

  function renderStatePicker(intentId) {
    appendFrankMessage("assistant", intentId === "readiness_check" ? "First, tell me which state you're applying in." : "Start with the state where you're applying.");
    setView(
      `
        <label class="frank-widget__field">
          <span>State</span>
          <select data-frank-state-select></select>
        </label>
        <div class="frank-widget__footer-actions">
          <button type="button" class="frank-widget__primary" data-frank-state-next data-frank-intent-next="${escapeHtml(intentId)}">Continue</button>
        </div>
      `
    );
    populateStateOptions(body.querySelector("[data-frank-state-select]"), frankState.stateCode || "CA");
  }

  function renderLicensePicker(intentId, options = {}) {
    const stateCode = options.stateCode || body.querySelector("[data-frank-state-select]")?.value || frankState.stateCode || "CA";
    const stateLabel = findStateLabel(stateCode);
    frankState = writeFrankState({ currentIntent: intentId, stateCode, stateLabel });
    const intro =
      options.intro ||
      (intentId === "readiness_check"
        ? `${stateLabel}, got it. Which license are you interested in?`
        : `${stateLabel}, got it. Which license are you interested in?`);
    appendFrankMessage("assistant", intro);
    setView(
      `
        <label class="frank-widget__field">
          <span>License</span>
          <select data-frank-license-select></select>
        </label>
        <div class="frank-widget__footer-actions">
          <button type="button" class="frank-widget__primary" data-frank-license-next data-frank-intent-next="${escapeHtml(intentId)}">Keep going</button>
          ${
            options.allowOtherState
              ? `<button type="button" class="frank-widget__secondary" data-frank-other-state>I'm in another state</button>`
              : frankState.stateCode
                ? `<button type="button" class="frank-widget__secondary" data-frank-change-state>Change state</button>`
                : ""
          }
        </div>
      `
    );
    populateLicenseOptions(
      body.querySelector("[data-frank-license-select]"),
      getLicenseChoicesForState(stateCode, offers),
      frankState.licenseSlug || "",
      "Select your license"
    );
  }

  function renderFindLicenseResult() {
    const selection = currentSelection();
    frankState = writeFrankState({
      currentIntent: "find_license",
      requestedLabel: selection.requestedLabel,
      available: selection.available
    });
    const bodyCopy = selection.available
      ? `The contractor course for ${selection.licenseLabel} is available now in ${selection.stateLabel}. If you want, I can send the direct link to your inbox so you can come back to it later, or you can start right now.`
      : `Awesome, we're just about to push our ${selection.licenseLabel} course. Share your email and we can save you a spot and send you next steps.`;
    appendFrankMessage("assistant", bodyCopy);

    setView(
      `
        <div class="frank-widget__card">
          <strong>${escapeHtml(selection.requestedLabel)}</strong>
          <span>${selection.available ? "This is the study and guidance path." : "This is the launch path for this course."}</span>
        </div>
        <div class="frank-widget__footer-actions">
          <a class="frank-widget__primary" href="${escapeHtml(selection.available ? selection.productUrl : config.faqUrl)}">
            ${selection.available ? "Click here to start" : "Read the FAQ first"}
          </a>
          <button type="button" class="frank-widget__secondary" data-frank-capture="${selection.available ? "email_link" : "waitlist"}">
            ${selection.available ? "Send me the link" : "Save my spot"}
          </button>
          <button type="button" class="frank-widget__secondary" data-frank-change-license>Change license</button>
        </div>
      `
    );
  }

  function renderReadinessQuestion() {
    const question = (config.readinessQuestions || [])[readinessStepIndex];
    if (!question) {
      renderReadinessResult();
      return;
    }

    appendFrankMessage("assistant", question.question);
    setView(
      `
        <div class="frank-widget__actions">
          ${question.choices
            .map(
              (choice) => `
                <button
                  type="button"
                  class="frank-widget__chip"
                  data-frank-readiness-choice
                  data-question-id="${escapeHtml(question.id)}"
                  data-choice-value="${escapeHtml(choice.value)}"
                >
                  ${escapeHtml(choice.label)}
                </button>
              `
            )
            .join("")}
        </div>
      `
    );
  }

  function renderReadinessResult() {
    const selection = currentSelection();
    const readinessStatus = computeFrankReadiness(readinessAnswers);
    frankState = writeFrankState({
      currentIntent: "readiness_check",
      requestedLabel: selection.requestedLabel,
      available: selection.available,
      experienceStatus: readinessAnswers.experienceStatus || null,
      readinessStatus
    });
    const segment = deriveFrankSegmentClient({
      stateCode: selection.stateCode,
      available: selection.available,
      readinessStatus
    });

    let eyebrow = "Worth checking first";
    let title = `You're making progress toward your ${selection.licenseLabel || "contractor"} license.`;
    let bodyCopy =
      "You do not need to hand over your email to keep exploring. If you want, I can send these next steps to your inbox so you do not lose track of them.";
    let primaryLabel = "Read the application guide";
    let primaryHref = config.applicationGuideUrl;

    if (!selection.available) {
      eyebrow = "Not live yet";
      title = `We saved the right path for ${selection.requestedLabel}.`;
      bodyCopy = `This license is not live yet, but you can still save your place. If you want updates later, I can send the right waitlist confirmation and follow-up.`;
      primaryLabel = "Read the FAQ";
      primaryHref = config.faqUrl;
    } else if (readinessStatus === "ready") {
      eyebrow = "Ready to move";
      title = `Congrats${frankState.firstName ? ` ${frankState.firstName}` : ""} on applying for your ${selection.licenseLabel} license.`;
      bodyCopy = "You look close enough to move forward. If you want, I can email the direct link and next steps so you can keep your momentum.";
      primaryLabel = "See this study guide";
      primaryHref = selection.productUrl;
    } else if (readinessStatus === "needs-work") {
      eyebrow = "Not yet";
      bodyCopy =
        "You flagged at least one thing that is worth handling before you pay for prep. That is still useful progress. I can point you to the application guide now, or email the next steps for later.";
    }

    appendFrankMessage("assistant", `${title} ${bodyCopy}`);
    setView(
      `
        <div class="frank-widget__card">
          <strong>${escapeHtml(selection.requestedLabel)}</strong>
          <span>${selection.available ? "This is the study and guidance path." : "This is the launch path for this course."}</span>
        </div>
        <div class="frank-widget__footer-actions">
          <a class="frank-widget__primary" href="${escapeHtml(primaryHref)}">${escapeHtml(primaryLabel)}</a>
          <button type="button" class="frank-widget__secondary" data-frank-capture="readiness_email">Email my next steps</button>
          <button type="button" class="frank-widget__secondary" data-frank-change-license>Change license</button>
        </div>
      `
    );
  }

  function renderFaqCategories() {
    appendFrankMessage("assistant", "Pick a topic and I’ll keep it simple.");
    setView(
      `
        <div class="frank-widget__actions">
          ${(config.faqCategories || [])
            .map(
              (category) => `
                <button type="button" class="frank-widget__chip" data-frank-faq-category="${escapeHtml(category.id)}">
                  ${escapeHtml(category.label)}
                </button>
              `
            )
            .join("")}
        </div>
      `
    );
  }

  function renderFaqCategory(categoryId) {
    const category = (config.faqCategories || []).find((item) => item.id === categoryId);
    const entries = (config.faq || []).filter((item) => item.category === categoryId);
    appendFrankMessage("user", category?.label || "FAQ");
    setView(
      `
        <div class="frank-widget__faq-list">
          ${entries
            .map(
              (entry) => `
                <button type="button" class="frank-widget__faq-item" data-frank-faq-id="${escapeHtml(entry.id)}">
                  ${escapeHtml(entry.question)}
                </button>
              `
            )
            .join("")}
        </div>
        <button type="button" class="frank-widget__secondary frank-widget__secondary--full" data-frank-back-to-faq>
          Back to FAQ topics
        </button>
      `
    );
  }

  function renderFaqAnswer(faqId) {
    const entry = (config.faq || []).find((item) => item.id === faqId);
    if (!entry) {
      renderFaqCategories();
      return;
    }

    appendFrankMessage("user", entry.question);
    appendFrankMessage("assistant", entry.answer);
    setView(
      `
        <div class="frank-widget__footer-actions">
          ${
            entry.primaryCtaHref
              ? `<a class="frank-widget__primary" href="${escapeHtml(entry.primaryCtaHref)}">${escapeHtml(entry.primaryCtaLabel || "Next step")}</a>`
              : ""
          }
          ${
            entry.secondaryCtaHref
              ? `<a class="frank-widget__secondary" href="${escapeHtml(entry.secondaryCtaHref)}">${escapeHtml(entry.secondaryCtaLabel || "More help")}</a>`
              : ""
          }
        </div>
        <button type="button" class="frank-widget__secondary frank-widget__secondary--full" data-frank-back-to-faq>
          Back to FAQ topics
        </button>
      `
    );
  }

  function renderStaticIntent(intentId) {
    const selection = currentSelection();
    if (intentId === "product_explainer") {
      appendFrankMessage("assistant", `Study Trades is built to help people move through exam prep and next-step licensing questions with more structure and less guesswork. ${selection.licenseLabel ? `For ${selection.licenseLabel},` : "For your license,"} the best next move is to look at the exact study and guidance path tied to your state and license.`);
      setView(
      `
          <div class="frank-widget__footer-actions">
            <a class="frank-widget__primary" href="${escapeHtml(config.pricingUrl)}">View pricing</a>
            <button type="button" class="frank-widget__secondary" data-frank-intent="find_license">Help me choose a license</button>
          </div>
        `
      );
      return;
    }

    appendFrankMessage("assistant", "At Study Trades, our motto is keep it simple. There is a flat fee for our guided online process to help you get licensed fast. First-time licensees in California usually need Law and Business in addition to their chosen license, for a combined total of $495. If you only need Law and Business, that is $250. If you are applying for multiple licenses, you would choose one primary license and then add sub-licenses for an additional fee per extra license.");
    setView(
      `
        <div class="frank-widget__footer-actions">
          <a class="frank-widget__primary" href="${escapeHtml(config.pricingUrl)}">View pricing</a>
          <button type="button" class="frank-widget__secondary" data-frank-intent="find_license">Help me choose a license</button>
        </div>
      `
    );
  }

  async function submitFrankLead(captureMode) {
    const selection = currentSelection();
    const firstName = String(body.querySelector("[data-frank-first-name]")?.value || "").trim();
    const email = String(body.querySelector("[data-frank-email]")?.value || "").trim().toLowerCase();
    const statusNode = body.querySelector("[data-frank-capture-status]");
    if (!email) {
      if (statusNode) statusNode.textContent = "Enter your email if you want me to send this to you.";
      return;
    }

    if (statusNode) statusNode.textContent = "Saving your next step...";

    const readinessStatus = frankState.readinessStatus || null;
    const experienceStatus = frankState.experienceStatus || null;
    const frankSegment = deriveFrankSegmentClient({
      stateCode: selection.stateCode,
      available: selection.available,
      readinessStatus
    });

    const waitlistSegment = frankSegment === "out_of_state_waitlist" || frankSegment === "ca_waitlist_unlaunched";
    const endpoint = waitlistSegment ? licenseInterestEndpoint : marketingLeadEndpoint;
    const source = waitlistSegment ? "shopify-waitlist" : readinessStatus ? "shopify-signup" : "shopify-site";

    try {
      const response = await studyTradesLeadRequest(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName || null,
          email,
          stateCode: selection.stateCode,
          licenseSlug: selection.licenseSlug || null,
          requestedLabel: selection.requestedLabel || null,
          source,
          marketingOptIn: true,
          readinessStatus,
          experienceStatus
        })
      });
      if (!response.ok) throw new Error("Lead capture failed");

      frankState = writeFrankState({ firstName: firstName || null, email, frankSegment });
      appendFrankMessage("assistant", waitlistSegment ? `You're on the list for ${selection.requestedLabel}. Watch for a welcome note and later launch updates.` : `Thanks${firstName ? ` ${firstName}` : ""}. I saved your next step for ${selection.requestedLabel}. Watch your inbox for the follow-up that matches where you are right now.`);
      setView(
      `
          <div class="frank-widget__footer-actions">
            <a class="frank-widget__primary" href="${escapeHtml(waitlistSegment ? config.faqUrl : selection.productUrl || config.pricingUrl)}">
              ${waitlistSegment ? "Read the FAQ" : "Keep going"}
            </a>
          </div>
        `
      );
    } catch (_error) {
      if (statusNode) statusNode.textContent = "I couldn't save that just yet. Try again in a moment.";
    }
  }

  function renderCapture(mode) {
    const selection = currentSelection();
    const readinessStatus = frankState.readinessStatus || null;
    const captureTitle =
      mode === "waitlist"
        ? `Want launch updates for ${selection.requestedLabel}?`
        : mode === "readiness_email"
          ? `Want the next steps for ${selection.licenseLabel || "this license"} emailed to you?`
          : `Want the direct link for ${selection.licenseLabel || "this license"} in your inbox?`;

    appendFrankMessage("assistant", captureTitle);
    setView(
      `
        <label class="frank-widget__field">
          <span>First name (optional)</span>
          <input type="text" data-frank-first-name value="${escapeHtml(frankState.firstName || "")}" />
        </label>
        <label class="frank-widget__field">
          <span>Email</span>
          <input type="email" data-frank-email value="${escapeHtml(frankState.email || "")}" />
        </label>
        <div class="frank-widget__footer-actions">
          <button type="button" class="frank-widget__primary" data-frank-submit-capture="${escapeHtml(mode)}">
            ${mode === "waitlist" ? "Save my spot" : readinessStatus ? "Email my next steps" : "Send it to me"}
          </button>
          <button type="button" class="frank-widget__secondary" data-frank-skip-capture>Skip for now</button>
        </div>
        <div class="frank-widget__status" data-frank-capture-status></div>
      `
    );
  }

  launcher.addEventListener("click", () => {
    if (widget.classList.contains("is-open")) {
      closeFrank();
      return;
    }
    openFrank();
    renderHome();
  });

  closeButton.addEventListener("click", closeFrank);

  body.addEventListener("click", (event) => {
    const target = event.target.closest("[data-frank-intent], [data-frank-resume], [data-frank-state-next], [data-frank-license-next], [data-frank-readiness-choice], [data-frank-faq-category], [data-frank-faq-id], [data-frank-back-to-faq], [data-frank-capture], [data-frank-submit-capture], [data-frank-skip-capture], [data-frank-change-state], [data-frank-other-state], [data-frank-change-license]");
    if (!target) return;

    if (target.hasAttribute("data-frank-resume")) {
      const intent = frankState.currentIntent || "find_license";
      if (intent === "readiness_check" && frankState.licenseSlug) {
        readinessAnswers = {
          experienceStatus: frankState.experienceStatus || null
        };
        renderReadinessResult();
        return;
      }
      if (intent === "faq") {
        renderFaqCategories();
        return;
      }
      renderFindLicenseResult();
      return;
    }

    const intentId = target.getAttribute("data-frank-intent");
    if (intentId) {
      appendFrankMessage("user", target.textContent.trim());
      if (intentId === "faq") {
        frankState = writeFrankState({ currentIntent: "faq" });
        renderFaqCategories();
        return;
      }
      if (intentId === "product_explainer" || intentId === "pricing_help") {
        frankState = writeFrankState({ currentIntent: intentId });
        renderStaticIntent(intentId);
        return;
      }
      if (intentId === "readiness_check") {
        frankState = writeFrankState({ currentIntent: intentId, stateCode: "CA", stateLabel: "California" });
        renderLicensePicker(intentId, {
          stateCode: "CA",
          allowOtherState: true,
          intro: "We're soft launching with just California. Select the license you want."
        });
        return;
      }
      renderStatePicker(intentId);
      return;
    }

    if (target.hasAttribute("data-frank-state-next")) {
      const stateSelect = body.querySelector("[data-frank-state-select]");
      const stateLabel = findStateLabel(stateSelect?.value || "CA");
      appendFrankMessage("user", stateLabel);
      renderLicensePicker(target.getAttribute("data-frank-intent-next") || "find_license");
      return;
    }

    if (target.hasAttribute("data-frank-change-state")) {
      renderStatePicker(frankState.currentIntent || "find_license");
      return;
    }

    if (target.hasAttribute("data-frank-other-state")) {
      appendFrankMessage("user", "I'm in another state");
      frankState = writeFrankState({ stateCode: "WA", stateLabel: "Washington", available: false });
      renderStatePicker("find_license");
      return;
    }

    if (target.hasAttribute("data-frank-license-next")) {
      const selectionIntent = target.getAttribute("data-frank-intent-next") || "find_license";
      const selectionState = currentSelection().stateCode;
      const licenseSelect = body.querySelector("[data-frank-license-select]");
      const licenseSlug = licenseSelect?.value || "";
      const licenseChoice = getLicenseChoicesForState(selectionState, offers).find((item) => item.slug === licenseSlug);

      if (!licenseSlug || !licenseChoice) return;
      appendFrankMessage("user", licenseChoice.label);

      const offer = findOfferForSelection(offers, selectionState, licenseSlug);
      frankState = writeFrankState({
        currentIntent: selectionIntent,
        licenseSlug,
        licenseLabel: licenseChoice.label,
        requestedLabel: `${findStateLabel(selectionState)} · ${licenseChoice.label}`,
        available: Boolean(offer?.available)
      });

      if (selectionIntent === "readiness_check" && !Boolean(offer?.available)) {
        renderReadinessResult();
        return;
      }

      if (selectionIntent === "readiness_check") {
        readinessStepIndex = 0;
        readinessAnswers = {};
        renderReadinessQuestion();
        return;
      }

      renderFindLicenseResult();
      return;
    }

    if (target.hasAttribute("data-frank-change-license")) {
      renderLicensePicker(frankState.currentIntent || "find_license", { stateCode: currentSelection().stateCode });
      return;
    }

    if (target.hasAttribute("data-frank-readiness-choice")) {
      const questionId = target.getAttribute("data-question-id");
      const choiceValue = target.getAttribute("data-choice-value");
      if (!questionId || !choiceValue) return;
      readinessAnswers[questionId] = choiceValue;
      appendFrankMessage("user", target.textContent.trim());
      readinessStepIndex += 1;
      renderReadinessQuestion();
      return;
    }

    const faqCategory = target.getAttribute("data-frank-faq-category");
    if (faqCategory) {
      renderFaqCategory(faqCategory);
      return;
    }

    const faqId = target.getAttribute("data-frank-faq-id");
    if (faqId) {
      renderFaqAnswer(faqId);
      return;
    }

    if (target.hasAttribute("data-frank-back-to-faq")) {
      renderFaqCategories();
      return;
    }

    const captureMode = target.getAttribute("data-frank-capture");
    if (captureMode) {
      appendFrankMessage("user", target.textContent.trim());
      renderCapture(captureMode);
      return;
    }

    const submitCaptureMode = target.getAttribute("data-frank-submit-capture");
    if (submitCaptureMode) {
      submitFrankLead(submitCaptureMode);
      return;
    }

    if (target.hasAttribute("data-frank-skip-capture")) {
      renderHome();
    }
  });

  loadStudyTradesOffers()
    .then((loadedOffers) => {
      offers = loadedOffers;
      renderHome();
      closeFrank();
    })
    .catch(() => {
      offers = createDefaultStudyTradesOffers();
      renderHome();
      closeFrank();
    });
}
